import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createClient } from '@supabase/supabase-js';

/* ══════════════════════════════════════════════════════════════════
   SUPABASE CLIENT
   Fix #9 Opus: guard su env vars vuote — mostra warning in console
   invece di far crashare silenziosamente tutte le chiamate API.
══════════════════════════════════════════════════════════════════ */
const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!SUPABASE_URL || !SUPABASE_ANON) {
  console.warn(
    '[EasyOrderHub] Variabili Supabase mancanti.\n' +
    'Imposta REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY nel file .env\n' +
    "L'app funzionerà in modalità offline (solo localStorage)."
  );
}

const SUPABASE_OK = Boolean(SUPABASE_URL && SUPABASE_ANON);

/* Crea il client solo se le variabili esistono — evita il crash
   "supabaseUrl is required" quando le env vars non sono settate */
const supabase = SUPABASE_OK
  ? createClient(SUPABASE_URL, SUPABASE_ANON)
  : null;

/* ══════════════════════════════════════════════════════════════════
   SUPABASE DATA LAYER
   Tutte le funzioni async per leggere/scrivere su Supabase.
   Manteniamo localStorage come cache locale per velocità.
══════════════════════════════════════════════════════════════════ */

/* Registra un nuovo fornitore: crea l'utente Auth + record DB */
async function dbRegisterSupplier(form, extraData) {
  if (!supabase) throw new Error('Supabase non configurato');
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: form.email,
    password: form.password,
  });
  if (authError) throw authError;

  const userId = authData.user.id;
  const { error: supplierError } = await supabase.from('suppliers').insert({
    id: userId,
    slug: form.slug,
    name: form.name,
    bio: form.bio || '',
    email: form.email,
    password_hash: '***',
    whatsapp: extraData.whatsapp || '',
    wa_country: form.waCountry || 'IT',
    wa_number: form.waNumber || '',
    delivery: form.delivery || '',
    payments: form.payments || [],
    plan: form.plan || 'base',
    default_lang: form.defaultLang || 'it',
  });
  if (supplierError) throw supplierError;
  return userId;
}

/* Login fornitore con email/password */
async function dbLoginSupplier(email, password) {
  if (!supabase) throw new Error('Supabase non configurato');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.user;
}

/* Logout */
async function dbLogout() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

/* Recupera la sessione auth corrente */
async function dbGetSession() {
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/* Salva un ordine su Supabase */
async function dbSaveOrder(supplierId, order) {
  if (!supabase || !supplierId) return;
  const { error } = await supabase.from('orders').insert({
    id: order.id,
    supplier_id: supplierId,
    customer_name: order.customer.name,
    customer_address: order.customer.address,
    customer_phone: order.customer.phone,
    customer_payment: order.customer.payment,
    customer_notes: order.customer.notes || '',
    items: order.items,
    total: order.total,
    status: 'pending',
  });
  if (error) console.warn('Supabase order save:', error.message);
}

/* Carica gli ordini dal DB per un fornitore */
async function dbLoadOrders(supplierId) {
  if (!supabase || !supplierId) return [];
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('supplier_id', supplierId)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data.map((row) => ({
    id: row.id,
    timestamp: row.created_at,
    customer: {
      name: row.customer_name,
      address: row.customer_address,
      phone: row.customer_phone,
      payment: row.customer_payment,
      notes: row.customer_notes,
    },
    items: Array.isArray(row.items) ? row.items : [],
    total: parseFloat(row.total),
    status: row.status,
  }));
}

/* Aggiorna lo status di un ordine */
async function dbUpdateOrderStatus(orderId, status) {
  if (!supabase) return;
  const { error } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', orderId);
  if (error) console.warn('Supabase order status:', error.message);
}

/* Aggiorna il profilo del fornitore */
async function dbUpdateSupplierProfile(userId, updates) {
  if (!supabase || !userId) return;
  const { error } = await supabase
    .from('suppliers')
    .update(updates)
    .eq('id', userId);
  if (error) console.warn('Supabase profile update:', error.message);
}

/* Recupera il supplier ID da Supabase tramite slug */
async function dbGetSupplierIdBySlug(slug) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('suppliers')
    .select('id')
    .eq('slug', slug)
    .single();
  if (error || !data) return null;
  return data.id;
}


/* ══════════════════════════════════════════════════════════════════
   ROUTE DETECTION
   /admin  → SuperAdmin platform panel
   tutto il resto → App catalogo
══════════════════════════════════════════════════════════════════ */
const IS_ADMIN_ROUTE = window.location.pathname.startsWith("/admin");

/* ══════════════════════════════════════════════════════════════════
   PLATFORM CONFIG
══════════════════════════════════════════════════════════════════ */
const PLATFORM = {
  name: "EasyOrderHub",
  abbr: "EOH",
  domain: "easyorderhub.com",
  currency: "€",
  plans: [
    {
      id: "starter", name: "Starter", price: 0, period: "",
      color: "#6B7280", popular: false, autoStock: false, multilang: false,
      features: ["1 catalogo", "Max 15 prodotti", "1 sezione", "Link condivisibile", "Watermark EOH"],
    },
    {
      id: "base", name: "Base", price: 19, period: "/mese",
      color: "#1A6B3A", popular: true, autoStock: false, multilang: false,
      features: ["Prodotti illimitati", "Sezioni illimitate", "Nessun watermark", "Supporto email", "Gestione stock manuale"],
    },
    {
      id: "pro", name: "Pro", price: 29, period: "/mese",
      color: "#1A3A8F", popular: false, autoStock: true, multilang: true,
      features: ["Tutto di Base", "✅ Stock aggiornato all'ordine in tempo reale", "🌍 Multilingua (5 lingue)", "Analytics ordini", "Dominio custom", "Supporto prioritario"],
    },
  ],
};

/* ══════════════════════════════════════════════════════════════════
   PAESI CON PREFISSO
══════════════════════════════════════════════════════════════════ */
const COUNTRIES = [
  { code: "IT", flag: "🇮🇹", name: "Italia",      prefix: "39"  },
  { code: "FR", flag: "🇫🇷", name: "Francia",     prefix: "33"  },
  { code: "DE", flag: "🇩🇪", name: "Germania",    prefix: "49"  },
  { code: "ES", flag: "🇪🇸", name: "Spagna",      prefix: "34"  },
  { code: "GB", flag: "🇬🇧", name: "Regno Unito", prefix: "44"  },
  { code: "CH", flag: "🇨🇭", name: "Svizzera",    prefix: "41"  },
  { code: "AT", flag: "🇦🇹", name: "Austria",     prefix: "43"  },
  { code: "BE", flag: "🇧🇪", name: "Belgio",      prefix: "32"  },
  { code: "NL", flag: "🇳🇱", name: "Olanda",      prefix: "31"  },
  { code: "PT", flag: "🇵🇹", name: "Portogallo",  prefix: "351" },
  { code: "US", flag: "🇺🇸", name: "USA",         prefix: "1"   },
  { code: "CA", flag: "🇨🇦", name: "Canada",      prefix: "1"   },
  { code: "MA", flag: "🇲🇦", name: "Marocco",     prefix: "212" },
  { code: "TN", flag: "🇹🇳", name: "Tunisia",     prefix: "216" },
];

/* ══════════════════════════════════════════════════════════════════
   UI STRINGHE MULTILINGUA
══════════════════════════════════════════════════════════════════ */
const UI_STRINGS = {
  it: {
    backTo: "← EasyOrderHub",
    addToCart: "+ Aggiungi",
    available: "✓ Disponibile",
    soldOut: "✗ Esaurito",
    stockLeft: "Solo {n} {u} rimasti",
    stockQty: "{n} {u} disponibili",
    cart: "Il tuo ordine",
    cartEmpty: "Carrello vuoto",
    cartEmptySub: "Aggiungi prodotti dal catalogo",
    proceed: "Procedi con l\'ordine →",
    total: "Totale",
    deliveryTitle: "Dati di consegna",
    yourName: "Nome e cognome *",
    namePh: "Es. Maria Bianchi",
    address: "Indirizzo di consegna *",
    addressPh: "Via, numero civico, città",
    phone: "Numero di telefono *",
    phonePh: "Il tuo numero di cellulare",
    payment: "Modalità di pagamento",
    notes: "Note (facoltative)",
    notesPh: "Orario preferito, istruzioni...",
    sendOrder: "Invia il tuo ordine",
    sentTitle: "Ordine ricevuto! ✓",
    waBannerTitle: "Avvisa subito il tuo fornitore",
    waBannerText: "Per evitare ritardi o problemi, avvisa il fornitore del tuo nuovo ordine.",
    waNotifyBtn: "📱 Avvisa su WhatsApp",
    editOrder: "← Modifica ordine",
    maxReached: "Massimo raggiunto",
    availCount: "{n} disponibili",
    delivery: "🚚",
    translating: "Traduzione in corso...",
    orderRegistered: "Ordine registrato",
    orderSummary: "Riepilogo ordine",
    backToCatalog: "Torna al catalogo →",
    stockValidationTitle: "Aggiornamento disponibilità",
    stockValidationRetry: "Modifica il carrello e riprova.",
    stockNoLongerAvail: "non è più disponibile",
    stockDeactivated: "è stato disattivato",
    stockRequested: "richiesti",
    stockNowAvailable: "disponibili ora",
    formRequired: "* Compila nome, indirizzo e telefono per procedere",
    itemsCount: "{n} articoli",
    colProduct: "Prodotto",
    colPrice: "Prezzo",
    colAvail: "Disponibilità",
    colQty: "Quantità",
  },
  en: {
    backTo: "← EasyOrderHub",
    addToCart: "+ Add",
    available: "✓ Available",
    soldOut: "✗ Sold out",
    stockLeft: "Only {n} {u} left",
    stockQty: "{n} {u} available",
    cart: "Your order",
    cartEmpty: "Empty cart",
    cartEmptySub: "Add products from the catalogue",
    proceed: "Continue →",
    total: "Total",
    deliveryTitle: "Delivery details",
    yourName: "Full name *",
    namePh: "E.g. John Smith",
    address: "Delivery address *",
    addressPh: "Street, number, city",
    phone: "Phone number *",
    phonePh: "Your mobile number",
    payment: "Payment method",
    notes: "Notes (optional)",
    notesPh: "Preferred time, instructions...",
    sendOrder: "Place your order",
    sentTitle: "Order received! ✓",
    waBannerTitle: "Notify your supplier now",
    waBannerText: "To avoid delays, notify the supplier about your new order.",
    waNotifyBtn: "📱 Notify via WhatsApp",
    editOrder: "← Edit order",
    maxReached: "Maximum reached",
    availCount: "{n} available",
    delivery: "🚚",
    translating: "Translating...",
    orderRegistered: "Order registered",
    orderSummary: "Order summary",
    backToCatalog: "Back to catalogue →",
    stockValidationTitle: "Availability update",
    stockValidationRetry: "Edit your cart and try again.",
    stockNoLongerAvail: "is no longer available",
    stockDeactivated: "has been deactivated",
    stockRequested: "requested",
    stockNowAvailable: "available now",
    formRequired: "* Please fill in name, address and phone number to continue",
    itemsCount: "{n} items",
    colProduct: "Product",
    colPrice: "Price",
    colAvail: "Availability",
    colQty: "Quantity",
  },
  fr: {
    backTo: "← EasyOrderHub",
    addToCart: "+ Ajouter",
    available: "✓ Disponible",
    soldOut: "✗ Épuisé",
    stockLeft: "Seulement {n} {u} restants",
    stockQty: "{n} {u} disponibles",
    cart: "Votre commande",
    cartEmpty: "Panier vide",
    cartEmptySub: "Ajoutez des produits du catalogue",
    proceed: "Continuer →",
    total: "Total",
    deliveryTitle: "Infos de livraison",
    yourName: "Nom complet *",
    namePh: "Ex. Marie Dupont",
    address: "Adresse de livraison *",
    addressPh: "Rue, numéro, ville",
    phone: "Numéro de téléphone *",
    phonePh: "Votre numéro de portable",
    payment: "Mode de paiement",
    notes: "Notes (optionnel)",
    notesPh: "Horaire préféré...",
    sendOrder: "Passer la commande",
    sentTitle: "Commande reçue ! ✓",
    waBannerTitle: "Avisez votre fournisseur",
    waBannerText: "Pour éviter tout retard, avisez le fournisseur de votre nouvelle commande.",
    waNotifyBtn: "📱 Aviser via WhatsApp",
    editOrder: "← Modifier",
    maxReached: "Maximum atteint",
    availCount: "{n} disponibles",
    delivery: "🚚",
    translating: "Traduction en cours...",
    orderRegistered: "Commande enregistrée",
    orderSummary: "Récapitulatif",
    backToCatalog: "Retour au catalogue →",
    stockValidationTitle: "Mise à jour des disponibilités",
    stockValidationRetry: "Modifiez votre panier et réessayez.",
    stockNoLongerAvail: "n\'est plus disponible",
    stockDeactivated: "a été désactivé",
    stockRequested: "demandés",
    stockNowAvailable: "disponibles maintenant",
    formRequired: "* Veuillez remplir le nom, l\'adresse et le téléphone pour continuer",
    itemsCount: "{n} articles",
    colProduct: "Produit",
    colPrice: "Prix",
    colAvail: "Disponibilité",
    colQty: "Quantité",
  },
  de: {
    backTo: "← EasyOrderHub",
    addToCart: "+ Hinzufügen",
    available: "✓ Verfügbar",
    soldOut: "✗ Ausverkauft",
    stockLeft: "Nur noch {n} {u}",
    stockQty: "{n} {u} verfügbar",
    cart: "Ihre Bestellung",
    cartEmpty: "Leerer Warenkorb",
    cartEmptySub: "Produkte hinzufügen",
    proceed: "Weiter →",
    total: "Gesamt",
    deliveryTitle: "Lieferdetails",
    yourName: "Vollständiger Name *",
    namePh: "z.B. Anna Müller",
    address: "Lieferadresse *",
    addressPh: "Straße, Hausnummer, Stadt",
    phone: "Telefonnummer *",
    phonePh: "Ihre Handynummer",
    payment: "Zahlungsart",
    notes: "Hinweise (optional)",
    notesPh: "Bevorzugte Uhrzeit...",
    sendOrder: "Bestellung aufgeben",
    sentTitle: "Bestellung eingegangen! ✓",
    waBannerTitle: "Benachrichtigen Sie den Anbieter",
    waBannerText: "Um Verzögerungen zu vermeiden, benachrichtigen Sie den Anbieter.",
    waNotifyBtn: "📱 Per WhatsApp benachrichtigen",
    editOrder: "← Bearbeiten",
    maxReached: "Maximum erreicht",
    availCount: "{n} verfügbar",
    delivery: "🚚",
    translating: "Übersetze...",
    orderRegistered: "Bestellung registriert",
    orderSummary: "Bestellübersicht",
    backToCatalog: "Zurück zum Katalog →",
    stockValidationTitle: "Verfügbarkeitsupdate",
    stockValidationRetry: "Bitte bearbeiten Sie den Warenkorb und versuchen Sie es erneut.",
    stockNoLongerAvail: "ist nicht mehr verfügbar",
    stockDeactivated: "wurde deaktiviert",
    stockRequested: "angefordert",
    stockNowAvailable: "jetzt verfügbar",
    formRequired: "* Bitte Name, Adresse und Telefon ausfüllen um fortzufahren",
    itemsCount: "{n} Artikel",
    colProduct: "Produkt",
    colPrice: "Preis",
    colAvail: "Verfügbarkeit",
    colQty: "Menge",
  },
  es: {
    backTo: "← EasyOrderHub",
    addToCart: "+ Añadir",
    available: "✓ Disponible",
    soldOut: "✗ Agotado",
    stockLeft: "Solo quedan {n} {u}",
    stockQty: "{n} {u} disponibles",
    cart: "Tu pedido",
    cartEmpty: "Carrito vacío",
    cartEmptySub: "Añade productos del catálogo",
    proceed: "Continuar →",
    total: "Total",
    deliveryTitle: "Datos de entrega",
    yourName: "Nombre completo *",
    namePh: "Ej. María García",
    address: "Dirección de entrega *",
    addressPh: "Calle, número, ciudad",
    phone: "Número de teléfono *",
    phonePh: "Tu número de móvil",
    payment: "Método de pago",
    notes: "Notas (opcional)",
    notesPh: "Horario preferido...",
    sendOrder: "Realizar pedido",
    sentTitle: "¡Pedido recibido! ✓",
    waBannerTitle: "Avisa a tu proveedor ahora",
    waBannerText: "Para evitar retrasos, avisa al proveedor de tu nuevo pedido.",
    waNotifyBtn: "📱 Avisar por WhatsApp",
    editOrder: "← Editar",
    maxReached: "Máximo alcanzado",
    availCount: "{n} disponibles",
    delivery: "🚚",
    translating: "Traduciendo...",
    orderRegistered: "Pedido registrado",
    orderSummary: "Resumen del pedido",
    backToCatalog: "Volver al catálogo →",
    stockValidationTitle: "Actualización de disponibilidad",
    stockValidationRetry: "Edita tu carrito e inténtalo de nuevo.",
    stockNoLongerAvail: "ya no está disponible",
    stockDeactivated: "ha sido desactivado",
    stockRequested: "solicitados",
    stockNowAvailable: "disponibles ahora",
    formRequired: "* Por favor completa nombre, dirección y teléfono para continuar",
    itemsCount: "{n} artículos",
    colProduct: "Producto",
    colPrice: "Precio",
    colAvail: "Disponibilidad",
    colQty: "Cantidad",
  },
};
const LANGS = {
  it: "🇮🇹 Italiano",
  en: "🇬🇧 English",
  fr: "🇫🇷 Français",
  de: "🇩🇪 Deutsch",
  es: "🇪🇸 Español",
};

function tl(lang, key, vars = {}) {
  const strings = UI_STRINGS[lang] || UI_STRINGS.it;
  let s = strings[key] || UI_STRINGS.it[key] || key;
  Object.entries(vars).forEach(([k, v]) => {
    s = s.replace("{" + k + "}", v);
  });
  return s;
}

/* ══════════════════════════════════════════════════════════════════
   FIX 5.6 — MESSAGGI WHATSAPP PER LINGUA DEL FORNITORE
   Il messaggio viene costruito nella lingua di supplier.defaultLang,
   impostata dal fornitore alla registrazione o nelle Impostazioni.
   È indipendente dalla lingua scelta dal cliente nel catalogo.
══════════════════════════════════════════════════════════════════ */
const WA_MESSAGES = {
  it: {
    greeting:      (name) => "Ciao " + name + "! 👋",
    intro:         "Ho appena effettuato un ordine sul tuo catalogo EOH.",
    orderLabel:    "Ordine",
    totalLabel:    "Totale",
    paymentLabel:  "Pagamento",
    deliveryLabel: "Consegna",
    nameLabel:     "Nome",
    addressLabel:  "Indirizzo",
    phoneLabel:    "Telefono",
    notesLabel:    "Note",
    closing:       "Trovi i dettagli nel tuo pannello ordini. Grazie! 🙏",
  },
  en: {
    greeting:      (name) => "Hi " + name + "! 👋",
    intro:         "I just placed an order on your EOH catalogue.",
    orderLabel:    "Order",
    totalLabel:    "Total",
    paymentLabel:  "Payment",
    deliveryLabel: "Delivery",
    nameLabel:     "Name",
    addressLabel:  "Address",
    phoneLabel:    "Phone",
    notesLabel:    "Notes",
    closing:       "You can find the details in your order panel. Thank you! 🙏",
  },
  fr: {
    greeting:      (name) => "Bonjour " + name + " ! 👋",
    intro:         "Je viens de passer une commande sur votre catalogue EOH.",
    orderLabel:    "Commande",
    totalLabel:    "Total",
    paymentLabel:  "Paiement",
    deliveryLabel: "Livraison",
    nameLabel:     "Nom",
    addressLabel:  "Adresse",
    phoneLabel:    "Téléphone",
    notesLabel:    "Notes",
    closing:       "Vous trouverez les détails dans votre panneau de commandes. Merci ! 🙏",
  },
  de: {
    greeting:      (name) => "Hallo " + name + "! 👋",
    intro:         "Ich habe gerade eine Bestellung in Ihrem EOH-Katalog aufgegeben.",
    orderLabel:    "Bestellung",
    totalLabel:    "Gesamt",
    paymentLabel:  "Zahlung",
    deliveryLabel: "Lieferung",
    nameLabel:     "Name",
    addressLabel:  "Adresse",
    phoneLabel:    "Telefon",
    notesLabel:    "Hinweise",
    closing:       "Die Details finden Sie in Ihrem Bestellpanel. Danke! 🙏",
  },
  es: {
    greeting:      (name) => "¡Hola " + name + "! 👋",
    intro:         "Acabo de realizar un pedido en tu catálogo EOH.",
    orderLabel:    "Pedido",
    totalLabel:    "Total",
    paymentLabel:  "Pago",
    deliveryLabel: "Entrega",
    nameLabel:     "Nombre",
    addressLabel:  "Dirección",
    phoneLabel:    "Teléfono",
    notesLabel:    "Notas",
    closing:       "Encontrarás los detalles en tu panel de pedidos. ¡Gracias! 🙏",
  },
};

/* ══════════════════════════════════════════════════════════════════
   STORAGE
══════════════════════════════════════════════════════════════════ */
function storageKey(slug) {
  return "eoh_v9_" + slug;
}

function loadSupplier(slug) {
  try {
    /* FIX N6 — prova prima la chiave v9, poi la v8 come fallback per
       non perdere i dati degli utenti esistenti della versione precedente */
    const rawV9 = localStorage.getItem("eoh_v9_" + slug);
    if (rawV9) return JSON.parse(rawV9);
    const rawV8 = localStorage.getItem("eoh_v8_" + slug);
    if (rawV8) {
      const data = JSON.parse(rawV8);
      saveSupplier(data); /* migra automaticamente alla chiave v9 */
      return data;
    }
    return makeDefaultSupplier(slug);
  } catch {
    return makeDefaultSupplier(slug);
  }
}

function saveSupplier(data) {
  try {
    localStorage.setItem(storageKey(data.slug), JSON.stringify(data));
  } catch {}
}

function uid() {
  return Math.random().toString(36).slice(2, 8);
}

function makeDefaultSupplier(slug) {
  return {
    slug,
    name: "La Bottega di Terra",
    bio: "Piccola azienda agricola familiare in Liguria. Coltiviamo senza pesticidi, rispettiamo la stagionalità e consegniamo a casa tua.",
    whatsapp: "393331234567",
    waCountry: "IT",
    waNumber: "3331234567",
    email: "info@labottega.it",
    password: "terra2024",
    delivery: "Consegne lun–sab · Ordina entro le 18:00 per il giorno successivo",
    payments: ["Contanti alla consegna", "Bonifico bancario"],
    plan: "pro",
    defaultLang: "it",
    pendingOrders: [],
    tables: [
      {
        id: "t1",
        name: "Ortofrutta",
        rows: [
          { id: "r1", emoji: "🍅", name: "Pomodori Cuore di Bue",  desc: "Raccolti stamattina, zero pesticidi",    price: 3.50,  unit: "kg",            stock: 8,    on: true  },
          { id: "r2", emoji: "🥒", name: "Zucchine Trombette",     desc: "Varietà antica ligure",                  price: 2.20,  unit: "kg",            stock: 10,   on: true  },
          { id: "r3", emoji: "🥗", name: "Insalata Mista",         desc: "Mix di foglie fresche del giorno",       price: 1.50,  unit: "busta",          stock: 0,    on: false },
          { id: "r4", emoji: "🍑", name: "Pesche Tabacchiere",     desc: "Solo disponibili d'estate",              price: 4.00,  unit: "kg",            stock: 15,   on: true  },
        ],
      },
      {
        id: "t2",
        name: "Conserve & Latticini",
        rows: [
          { id: "r6", emoji: "🧀", name: "Ricotta Fresca",         desc: "Produzione propria, consegnata fresca",  price: 5.00,  unit: "500g",           stock: 6,    on: true  },
          { id: "r7", emoji: "🫙", name: "Confettura di Fichi",    desc: "Ricetta della nonna",                    price: 5.50,  unit: "vasetto 250g",   stock: 20,   on: true  },
          { id: "r8", emoji: "🥚", name: "Uova Fresche",           desc: "Galline allevate a terra all'aperto",    price: 3.00,  unit: "cartone 6pz",    stock: null, on: true  },
        ],
      },
      {
        id: "t3",
        name: "Spezie & Infusi",
        rows: [
          { id: "r9",  emoji: "🌸", name: "Zafferano Locale",      desc: "Filamenti essiccati, raccolta manuale",  price: 12.00, unit: "busta 0.5g",    stock: 30,   on: true  },
          { id: "r10", emoji: "🌿", name: "Tisana Montagna",        desc: "Mix di erbe spontanee",                  price: 4.50,  unit: "sacchetto 50g",  stock: null, on: true  },
        ],
      },
    ],
  };
}

/* ══════════════════════════════════════════════════════════════════
   STOCK HELPERS
══════════════════════════════════════════════════════════════════ */
function canAdd(row, inCart) {
  if (!row.on) return false;
  if (row.stock === null) return true;
  if (row.stock <= 0) return false;
  return inCart < row.stock;
}

function getStockLabel(row, lang) {
  if (!row.on || row.stock === 0) return null;
  if (row.stock === null) return null;
  if (row.stock <= 3) return { text: tl(lang, "stockLeft", { n: row.stock, u: row.unit }), urgent: true };
  return { text: tl(lang, "stockQty", { n: row.stock, u: row.unit }), urgent: false };
}

/* ══════════════════════════════════════════════════════════════════
   CLAUDE API TRANSLATION (Pro feature)
══════════════════════════════════════════════════════════════════ */
async function translateCatalog(tables, targetLang) {
  const langNames = { en: "English", fr: "French", de: "German", es: "Spanish" };
  const payload = tables.map((tb) => ({
    id: tb.id,
    tableName: tb.name,
    rows: tb.rows.map((r) => ({ id: r.id, name: r.name, desc: r.desc })),
  }));
  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      messages: [{
        role: "user",
        content: "Translate from Italian to " + langNames[targetLang] + ". Only translate 'tableName','name','desc'. Keep all id unchanged. Return ONLY valid JSON, no markdown.\n" + JSON.stringify(payload),
      }],
    }),
  });
  const data = await response.json();
  const raw = (data.content || []).find((b) => b.type === "text")?.text || "[]";
  return JSON.parse(raw.replace(/```json|```/g, "").trim());
}

/* ══════════════════════════════════════════════════════════════════
   GLOBAL CSS
══════════════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Outfit:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #F8F7F4; color: #1C1C1A; font-family: 'Outfit', sans-serif; -webkit-font-smoothing: antialiased; }
  h1, h2, h3, h4 { font-family: 'Fraunces', serif; }
  input, select, textarea, button { font-family: 'Outfit', sans-serif; }
  button { cursor: pointer; border: none; background: none; }

  @keyframes fadeUp   { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideIn  { from { transform: translateX(105%); }            to { transform: translateX(0); } }
  @keyframes fadeIn   { from { opacity: 0; }                             to { opacity: 1; } }
  @keyframes popIn    { 0% { transform: scale(.94); opacity: 0; }        100% { transform: scale(1); opacity: 1; } }
  @keyframes spin     { to { transform: rotate(360deg); } }
  @keyframes pulse    { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
  @keyframes bounceIn { 0% { transform: scale(.7); opacity: 0; } 60% { transform: scale(1.05); } 100% { transform: scale(1); opacity: 1; } }

  .fu   { animation: fadeUp   .3s ease both; }
  .si   { animation: slideIn  .28s cubic-bezier(.16,1,.3,1) both; }
  .fi   { animation: fadeIn   .22s ease both; }
  .pi   { animation: popIn    .28s cubic-bezier(.34,1.56,.64,1) both; }
  .bi   { animation: bounceIn .4s  cubic-bezier(.34,1.56,.64,1) both; }
  .spin { animation: spin     .8s  linear infinite; }

  /* Product table */
  .pt { width: 100%; border-collapse: collapse; font-size: 14px; }
  .pt th {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: .06em; color: #9A9A90;
    padding: 10px 14px; border-bottom: 1.5px solid #EDEAE4;
    text-align: left; white-space: nowrap;
  }
  .pt td { padding: 12px 14px; border-bottom: 1px solid #F0EDE8; vertical-align: middle; }
  .pt tbody tr:last-child td { border-bottom: none; }
  .pt tbody tr.off { opacity: .5; }
  .pt tbody tr:hover:not(.off) td { background: #FAFAF8; }

  /* Mobile: table → cards */
  @media (max-width: 640px) {
    .pt thead { display: none; }
    .pt, .pt tbody, .pt tr, .pt td { display: block; }
    .pt tbody tr {
      background: #fff; border: 1px solid #EDEAE4;
      border-radius: 12px; margin-bottom: 8px;
      padding: 12px 14px; position: relative;
    }
    .pt tbody tr:hover:not(.off) td { background: transparent; }
    .pt td { padding: 3px 0; border: none; }
    .td-name   { font-weight: 600; font-size: 14px; padding-right: 110px !important; }
    .td-action { position: absolute; top: 12px; right: 14px; }
    .td-hide   { display: none; }
  }

  /* Toggle switch */
  .tgl { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
  .tgl input { opacity: 0; width: 0; height: 0; }
  .tgl-sl { position: absolute; cursor: pointer; inset: 0; background: #D0CCC6; border-radius: 22px; transition: .22s; }
  .tgl-sl::before {
    content: ''; position: absolute;
    height: 16px; width: 16px; left: 3px; bottom: 3px;
    background: #fff; border-radius: 50%; transition: .22s;
  }
  input:checked + .tgl-sl { background: #1A6B3A; }
  input:checked + .tgl-sl::before { transform: translateX(18px); }

  /* Tab buttons */
  .tab-btn {
    padding: 8px 16px; border-radius: 8px;
    font-size: 13px; font-weight: 600; color: #6B6B65;
    transition: all .18s; white-space: nowrap;
  }
  .tab-btn.active {
    background: #fff; color: #1C1C1A;
    box-shadow: 0 1px 6px rgba(0,0,0,.08);
  }

  /* Base input */
  .inp {
    width: 100%; padding: 10px 13px;
    border: 1.5px solid #E0DAD4; border-radius: 9px;
    font-size: 14px; background: #fff; color: #1C1C1A;
    outline: none; transition: border-color .18s;
  }
  .inp:focus { border-color: #1A6B3A; }
  .inp.err   { border-color: #D04030; }
  .inp::placeholder { color: #B0A898; }

  /* Stock bar */
  .sb-wrap { height: 4px; background: #E8E5E0; border-radius: 2px; overflow: hidden; width: 72px; }
  .sb-fill  { height: 100%; border-radius: 2px; transition: width .4s ease; }

  /* WhatsApp banner */
  .wa-banner {
    background: linear-gradient(135deg, #1A4A2A, #1E5C30);
    border-radius: 14px; padding: 20px; border: 1px solid #2A6A3A;
  }

  /* SuperAdmin dark theme */
  body.admin-mode { background: #0F1117 !important; color: #E8E6E0 !important; }
  .sa-sidebar {
    width: 220px; background: #161920; border-right: 1px solid #252830;
    height: 100vh; position: fixed; top: 0; left: 0;
    display: flex; flex-direction: column; z-index: 50;
  }
  .sa-nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 18px; font-size: 13px; font-weight: 500;
    color: #8A8880; border-radius: 8px; margin: 1px 8px;
    transition: all .15s; cursor: pointer;
  }
  .sa-nav-item:hover, .sa-nav-item.active { background: #1E2230; color: #E8E6E0; }
  .sa-nav-badge {
    background: #E04040; color: #fff; border-radius: 10px;
    font-size: 10px; font-weight: 700; padding: 1px 6px; margin-left: auto;
  }
  .sa-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .sa-table th {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: .06em; color: #5A5A60;
    padding: 10px 14px; border-bottom: 1px solid #252830;
    text-align: left; white-space: nowrap;
    background: #161920; position: sticky; top: 0;
  }
  .sa-table td { padding: 11px 14px; border-bottom: 1px solid #1E2028; vertical-align: middle; }
  .sa-table tbody tr:hover td { background: #1A1D25; }
  .sa-table tbody tr:last-child td { border-bottom: none; }
  .sa-inp {
    width: 100%; padding: 9px 12px; border: 1px solid #2E3040;
    border-radius: 8px; font-size: 13px; background: #1E2028;
    color: #E8E6E0; outline: none; transition: border-color .15s;
  }
  .sa-inp:focus { border-color: #3A6BFF; }
  .sa-inp::placeholder { color: #4A4A55; }
  .sa-card { background: #161920; border: 1px solid #252830; border-radius: 12px; padding: 18px 20px; }
  .sa-panel { background: #161920; border: 1px solid #252830; border-radius: 12px; overflow: hidden; }
  .badge-active    { background: #1A3A1A; color: #60C870; border: 1px solid #2A5A2A; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
  .badge-suspended { background: #3A1A1A; color: #E06060; border: 1px solid #5A2A2A; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
  .badge-trial     { background: #2A2A1A; color: #E0B040; border: 1px solid #4A4A2A; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
  .plan-badge { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 8px; }
  .sa-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.7);
    z-index: 200; display: flex; align-items: center;
    justify-content: center; padding: 20px;
  }
  .sa-modal-box {
    background: #161920; border: 1px solid #2E3040;
    border-radius: 16px; width: 100%; max-width: 560px;
    max-height: 90vh; overflow-y: auto;
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: #F0EDE8; }
  ::-webkit-scrollbar-thumb { background: #C0B8A8; border-radius: 2px; }
`;

/* ══════════════════════════════════════════════════════════════════
   FIX 6.7 — ERROR BOUNDARY
══════════════════════════════════════════════════════════════════ */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("EOH ErrorBoundary:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", background: "#F8F7F4", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 36, maxWidth: 440, width: "100%", border: "1px solid #EDEAE4", textAlign: "center" }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, marginBottom: 10 }}>Qualcosa è andato storto</h2>
            <p style={{ fontSize: 14, color: "#6B6B65", lineHeight: 1.6, marginBottom: 24 }}>
              Si è verificato un errore imprevisto. I tuoi dati sono al sicuro nel browser.
            </p>
            <button
              style={{ background: "#0A2A1A", color: "#fff", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700 }}
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Riprova
            </button>
            {this.state.error && (
              <details style={{ marginTop: 20, textAlign: "left", fontSize: 11, color: "#9A9A90" }}>
                <summary style={{ cursor: "pointer" }}>Dettagli tecnici</summary>
                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", marginTop: 6 }}>{this.state.error.toString()}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ══════════════════════════════════════════════════════════════════
   SHARED ATOMS
══════════════════════════════════════════════════════════════════ */
function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: "#6B6B65", letterSpacing: ".05em", textTransform: "uppercase", marginBottom: 5 }}>
      {children}
    </div>
  );
}

function AvailBadge({ row, lang }) {
  const unavail = !row.on || (row.stock !== null && row.stock <= 0);
  return (
    <span style={{
      display: "inline-block", fontSize: 11, fontWeight: 600,
      padding: "3px 9px", borderRadius: 20, letterSpacing: ".03em",
      background: unavail ? "#F4E8E6" : "#E4F4E8",
      color: unavail ? "#A04030" : "#1A6B3A",
    }}>
      {unavail ? tl(lang, "soldOut") : tl(lang, "available")}
    </span>
  );
}

function StockBar({ row }) {
  if (row.stock === null) {
    return <span style={{ fontSize: 11, color: "#9A9A90", fontStyle: "italic" }}>∞</span>;
  }
  const pct = row.stock <= 0 ? 0 : Math.min(100, (row.stock / Math.max(row.stock, 20)) * 100);
  const color = row.stock <= 0 ? "#E8D4D0" : row.stock <= 3 ? "#E85030" : row.stock <= 8 ? "#E8A030" : "#1A6B3A";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div className="sb-wrap">
        <div className="sb-fill" style={{ width: pct + "%", background: color }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600 }}>
        {row.stock <= 0 ? "Esaurito" : row.stock}
      </span>
    </div>
  );
}

function Spinner() {
  return (
    <div
      className="spin"
      style={{ width: 16, height: 16, border: "2px solid #C0B8A8", borderTopColor: "#1A6B3A", borderRadius: "50%", display: "inline-block" }}
    />
  );
}

function InlineEdit({ value, onSave, type, width, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);

  function commit() { onSave(v); setEditing(false); }
  function cancel() { setV(value); setEditing(false); }

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "inherit" }}>
        <span
          onDoubleClick={() => setEditing(true)}
          style={{ cursor: "text", borderBottom: "1px dashed #C0B8A8", paddingBottom: 1 }}
        >
          {(value !== null && value !== undefined)
            ? value
            : <span style={{ color: "#C0B8A8", fontStyle: "italic" }}>{placeholder || "—"}</span>
          }
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Modifica"
          style={{
            background: "#F0EDE8", border: "1px solid #DDDAD4",
            borderRadius: 5, width: 26, height: 26,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, color: "#6B6B65", cursor: "pointer", flexShrink: 0,
          }}
        >
          ✏
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <input
        autoFocus
        type={type || "text"}
        step={type === "number" ? "0.5" : undefined}
        value={(v !== null && v !== undefined) ? v : ""}
        placeholder={placeholder || ""}
        onChange={(e) => {
          if (type === "number") {
            setV(e.target.value === "" ? null : parseFloat(e.target.value) || 0);
          } else {
            setV(e.target.value);
          }
        }}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
        style={{
          padding: "3px 8px", border: "1.5px solid #1A6B3A",
          borderRadius: 6, fontSize: 13,
          width: width || (type === "number" ? "72px" : "160px"),
          fontFamily: "'Outfit', sans-serif",
        }}
      />
      <button style={{ background: "#1A6B3A", color: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 700 }} onClick={commit}>✓</button>
      <button style={{ background: "#F0EDE8", color: "#6B6B65", borderRadius: 6, padding: "3px 8px", fontSize: 12 }} onClick={cancel}>✕</button>
    </span>
  );
}

function ProGate({ feature, onUpgrade }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, border: "2px solid #1A3A8F", padding: 36, textAlign: "center" }}>
      <div style={{ fontSize: 42, marginBottom: 14 }}>🔒</div>
      <h3 style={{ fontSize: 22, marginBottom: 10, color: "#1A3A8F" }}>Funzione esclusiva Pro</h3>
      <p style={{ fontSize: 14, color: "#6B6B65", lineHeight: 1.7, maxWidth: 380, margin: "0 auto 24px" }}>
        <strong>{feature}</strong> è disponibile solo con il piano Pro (€29/mese).
      </p>
      <div style={{ background: "#EEF2FF", borderRadius: 12, padding: "14px 20px", marginBottom: 20, textAlign: "left", maxWidth: 360, margin: "0 auto 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1A3A8F", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".05em" }}>Piano Pro include:</div>
        {[
          "✅ Gestione ordini con pannello dedicato (Pro)",
          "🌍 Catalogo multilingua (IT/EN/FR/DE/ES)",
          "📊 Analytics ordini",
          "🔗 Dominio custom",
        ].map((f, i) => (
          <div key={i} style={{ fontSize: 13, color: "#1C1C1A", padding: "3px 0" }}>{f}</div>
        ))}
      </div>
      <button
        onClick={onUpgrade}
        style={{ background: "#1A3A8F", color: "#fff", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700 }}
      >
        Passa a Pro — €29/mese →
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PHONE INPUT con selector nazione (riusabile)
══════════════════════════════════════════════════════════════════ */
function PhoneInput({ value, country, onValueChange, onCountryChange, error, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const selC = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];

  useEffect(() => {
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const filtered = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.prefix.includes(search) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  function handleNumChange(e) {
    onValueChange(e.target.value.replace(/[^0-9]/g, ""));
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ display: "flex", border: "1.5px solid " + (error ? "#D04030" : "#E0DAD4"), borderRadius: 9, background: "#fff", transition: "border-color .18s" }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 12px", borderRight: "1px solid #E0DAD4", background: "#F8F7F4", fontSize: 14, whiteSpace: "nowrap", flexShrink: 0, minWidth: 96, borderRadius: "8px 0 0 8px" }}
        >
          <span style={{ fontSize: 20 }}>{selC.flag}</span>
          <span style={{ fontSize: 13, color: "#6B6B65", fontWeight: 500 }}>+{selC.prefix}</span>
          <span style={{ fontSize: 10, color: "#9A9A90" }}>▾</span>
        </button>
        <input
          type="tel"
          inputMode="numeric"
          placeholder={placeholder || "3331234567"}
          value={value}
          onChange={handleNumChange}
          style={{ flex: 1, padding: "10px 13px", border: "none", background: "transparent", fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif", color: "#1C1C1A" }}
        />
      </div>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300, background: "#fff", border: "1px solid #EDEAE4", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,.12)", width: 260, maxHeight: 280, overflowY: "auto" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #EDEAE4" }}>
            <input
              className="inp"
              placeholder="Cerca paese…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ padding: "7px 10px", fontSize: 13 }}
            />
          </div>
          {filtered.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onCountryChange(c.code); setOpen(false); setSearch(""); }}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px", fontSize: 13, color: "#1C1C1A", background: c.code === country ? "#F0F8F0" : "transparent", textAlign: "left" }}
            >
              <span style={{ fontSize: 18 }}>{c.flag}</span>
              <span style={{ flex: 1 }}>{c.name}</span>
              <span style={{ color: "#9A9A90", fontWeight: 600 }}>+{c.prefix}</span>
            </button>
          ))}
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: "#D04030", marginTop: 4 }}>⚠ {error}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   1. PLATFORM HOME
══════════════════════════════════════════════════════════════════ */
function PlatformHome({ onDemo, onRegister }) {
  return (
    <div style={{ minHeight: "100vh", background: "#F8F7F4" }}>
      {/* Nav */}
      <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(248,247,244,.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid #EDEAE4", padding: "0 24px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 700, color: "#0A2A1A", letterSpacing: "-.02em" }}>
          EasyOrderHub
          <span style={{ fontSize: 11, fontWeight: 400, color: "#9A9A90", marginLeft: 4, fontFamily: "'Outfit', sans-serif" }}>.com</span>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onDemo} style={{ padding: "8px 18px", borderRadius: 8, border: "1.5px solid #DDDAD4", fontSize: 13, fontWeight: 500, color: "#4A4A42" }}>Demo live</button>
          <button onClick={onRegister} style={{ padding: "8px 18px", borderRadius: 8, background: "#0A2A1A", fontSize: 13, fontWeight: 600, color: "#fff" }}>Registrati gratis</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ background: "linear-gradient(155deg,#0A2A1A 0%,#1A5A2A 55%,#2A7A3A 100%)", padding: "88px 24px 80px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: .05, backgroundImage: "radial-gradient(#fff 1px,transparent 1px)", backgroundSize: "32px 32px" }} />
        <div style={{ position: "relative", maxWidth: 680, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 20, padding: "5px 14px", fontSize: 12, color: "#80D890", letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 28 }}>
            <span style={{ animation: "pulse 2s ease-in-out infinite", display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#60C870" }} />
            EOH · easyorderhub.com
          </div>
          <h1 style={{ fontSize: "clamp(30px,6vw,54px)", fontWeight: 700, color: "#EFF8F0", lineHeight: 1.12, letterSpacing: "-.02em", marginBottom: 20 }}>
            Catalogo digitale.<br />Ordini su WhatsApp.<br />Stock sempre aggiornato.
          </h1>
          <p style={{ fontSize: 17, color: "#90C8A0", maxWidth: 500, margin: "0 auto 36px", lineHeight: 1.65 }}>
            Il tuo spazio online in 5 minuti. I clienti ordinano solo ciò che hai davvero disponibile. Zero commissioni. Zero app.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={onDemo} style={{ padding: "14px 30px", borderRadius: 10, background: "#5CC870", fontSize: 15, fontWeight: 700, color: "#fff" }}>
              Prova la demo →
            </button>
            <button onClick={onRegister} style={{ padding: "14px 30px", borderRadius: 10, border: "1.5px solid rgba(255,255,255,.2)", background: "transparent", fontSize: 15, fontWeight: 500, color: "#C0E8C8" }}>
              Crea il tuo catalogo
            </button>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section style={{ padding: "64px 24px", maxWidth: 820, margin: "0 auto" }}>
        <h2 style={{ textAlign: "center", fontSize: "clamp(24px,4vw,36px)", fontWeight: 700, marginBottom: 48 }}>
          Tre passi. Zero tecnicismi.
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 20 }}>
          {[
            ["01", "Registrati in 2 minuti",   "Inserisci nome, WhatsApp, info consegne. Ottieni subito il tuo link personale."],
            ["02", "Costruisci il catalogo",    "Aggiungi prodotti con prezzo, unità libera e stock. Aggiornabile in tempo reale."],
            ["03", "Ricevi gli ordini",         "I clienti scelgono — il sistema blocca gli ordini sopra la disponibilità e li salva nel tuo pannello."],
          ].map(([n, title, body], i) => (
            <div key={i} className="fu" style={{ animationDelay: i * .1 + "s", background: "#fff", borderRadius: 16, padding: 26, border: "1px solid #EDEAE4" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1A6B3A", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10 }}>{n}</div>
              <h3 style={{ fontSize: 19, fontWeight: 700, marginBottom: 10 }}>{title}</h3>
              <p style={{ fontSize: 14, color: "#6B6B65", lineHeight: 1.7 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section style={{ background: "#EDEAE4", padding: "64px 24px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(22px,4vw,34px)", fontWeight: 700, marginBottom: 48 }}>
            Semplice e trasparente
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
            {PLATFORM.plans.map((plan) => (
              <div key={plan.id} style={{ background: "#fff", borderRadius: 16, padding: 28, position: "relative", border: plan.popular ? "2px solid " + plan.color : "1px solid #EDEAE4" }}>
                {plan.popular && (
                  <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", background: plan.color, color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 16px", borderRadius: 20, whiteSpace: "nowrap" }}>
                    PIÙ SCELTO
                  </div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700, color: plan.color, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 8 }}>{plan.name}</div>
                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 700 }}>
                    {plan.price === 0 ? "Gratis" : "€" + plan.price}
                  </span>
                  {plan.period && <span style={{ fontSize: 13, color: "#9A9A90" }}>{plan.period}</span>}
                </div>
                <ul style={{ listStyle: "none", marginBottom: 24 }}>
                  {plan.features.map((f, j) => (
                    <li key={j} style={{ fontSize: 13, color: "#4A4A42", padding: "4px 0", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <span style={{ color: plan.color, flexShrink: 0 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onRegister}
                  style={{ width: "100%", padding: 11, borderRadius: 9, border: "1.5px solid", borderColor: plan.popular ? plan.color : "#DDDAD4", background: plan.popular ? plan.color : "transparent", color: plan.popular ? "#fff" : "#4A4A42", fontSize: 13, fontWeight: 600 }}
                >
                  {plan.price === 0 ? "Inizia gratis" : "Scegli " + plan.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer style={{ borderTop: "1px solid #EDEAE4", padding: "22px 24px", textAlign: "center", fontSize: 12, color: "#9A9A90" }}>
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: "#0A2A1A", marginRight: 4 }}>EasyOrderHub</span>
        · easyorderhub.com · Demo salvata nel browser
      </footer>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   2. REGISTRATION FORM — 4 step
══════════════════════════════════════════════════════════════════ */
function RegistrationForm({ onSuccess, onBack }) {
  const STEPS = ["Profilo pubblico", "Contatti & Accesso", "Logistica", "Piano"];
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [newPm, setNewPm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", bio: "",
    email: "", waCountry: "IT", waNumber: "",
    password: "", confirmPassword: "",
    delivery: "Consegne lun–sab · Ordina entro le 18:00",
    payments: ["Contanti alla consegna", "Bonifico bancario"],
    plan: "base",
    defaultLang: "it",
  });

  function autoSlug(name) {
    return name.toLowerCase().trim()
      .replace(/[àáâã]/g, "a").replace(/[èéêë]/g, "e")
      .replace(/[ìíîï]/g, "i").replace(/[òóôõ]/g, "o").replace(/[ùúûü]/g, "u")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  function setF(k, v) {
    setForm((f) => {
      const n = { ...f, [k]: v };
      if (k === "name") n.slug = autoSlug(v);
      return n;
    });
    setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  }

  const selC = COUNTRIES.find((c) => c.code === form.waCountry) || COUNTRIES[0];

  function validate() {
    const e = {};
    if (step === 0) {
      if (!form.name.trim()) e.name = "Il nome è obbligatorio";
      if (!form.slug.trim()) e.slug = "Lo slug è obbligatorio";
    }
    if (step === 1) {
      if (!form.email.includes("@")) e.email = "Inserisci un'email valida";
      if (!form.waNumber.trim()) e.waNumber = "Inserisci il numero WhatsApp";
      else if (!/^\d+$/.test(form.waNumber)) e.waNumber = "Solo cifre, senza spazi o simboli";
      else if (form.waNumber.length < 6) e.waNumber = "Numero troppo corto";
      if (!form.password) e.password = "Scegli una password";
      else if (form.password.length < 6) e.password = "Minimo 6 caratteri";
      if (form.password !== form.confirmPassword) e.confirmPassword = "Le password non corrispondono";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const demoTables = makeDefaultSupplier(form.slug).tables;
      const tablesForPlan = form.plan === "starter" ? [demoTables[0]] : demoTables;
      const whatsapp = selC.prefix + form.waNumber;

      /* Registra su Supabase Auth + DB — restituisce lo userId */
      let userId = null;
      if (SUPABASE_OK) {
        userId = await dbRegisterSupplier(form, { whatsapp });
      }

      /* Salva in localStorage con supplierId incluso — serve a CartDrawer
         per salvare gli ordini su Supabase (FIX #1 Opus review) */
      const newSupplier = {
        ...makeDefaultSupplier(form.slug),
        ...form,
        whatsapp,
        supplierId: userId,
        pendingOrders: [],
        tables: tablesForPlan,
      };
      saveSupplier(newSupplier);
      onSuccess(form.slug);
    } catch (err) {
      const msg = err.message || 'Errore durante la registrazione.';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        setSubmitError('Questa email è già registrata. Prova ad accedere.');
      } else if (msg.includes('duplicate') || msg.includes('slug')) {
        setSubmitError('Questo URL catalogo è già in uso. Scegli un nome diverso.');
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function addPayment() {
    if (newPm.trim()) {
      setF("payments", [...form.payments, newPm.trim()]);
      setNewPm("");
    }
  }

  function removePayment(i) {
    setF("payments", form.payments.filter((_, j) => j !== i));
  }

  return (
    <div style={{ minHeight: "100vh", background: "#EDEAE4", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="pi" style={{ background: "#fff", borderRadius: 20, padding: "32px 36px", maxWidth: 520, width: "100%", border: "1px solid #EDEAE4" }}>
        {/* Progress bar */}
        <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? "#1A6B3A" : "#E0DAD4", transition: "background .3s" }} />
          ))}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#9A9A90", letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 6 }}>
          Passo {step + 1} di {STEPS.length}
        </div>
        <h2 style={{ fontSize: 24, marginBottom: 22 }}>{STEPS[step]}</h2>

        {/* Step 0: Profilo */}
        {step === 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <FieldLabel>Nome attività *</FieldLabel>
              <input
                className={"inp" + (errors.name ? " err" : "")}
                placeholder="Es. La Bottega di Terra"
                value={form.name}
                onChange={(e) => setF("name", e.target.value)}
              />
              {errors.name && <div style={{ fontSize: 12, color: "#D04030", marginTop: 4 }}>⚠ {errors.name}</div>}
            </div>
            <div>
              <FieldLabel>URL del tuo catalogo *</FieldLabel>
              <div style={{ display: "flex", alignItems: "center", background: "#F5F2EC", borderRadius: 9, border: "1.5px solid " + (errors.slug ? "#D04030" : "#E0DAD4"), overflow: "hidden" }}>
                <span style={{ padding: "10px 12px", fontSize: 12, color: "#9A9A90", whiteSpace: "nowrap", borderRight: "1px solid #E0DAD4", flexShrink: 0 }}>easyorderhub.com/</span>
                <input
                  style={{ flex: 1, padding: "10px 12px", border: "none", background: "transparent", fontSize: 14, color: "#1C1C1A", outline: "none", fontFamily: "'Outfit', sans-serif" }}
                  value={form.slug}
                  onChange={(e) => setF("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="es. la-bottega"
                />
              </div>
              <p style={{ fontSize: 11, color: "#9A9A90", marginTop: 4 }}>Non modificabile dopo la registrazione. Generato automaticamente dal nome.</p>
              {errors.slug && <div style={{ fontSize: 12, color: "#D04030", marginTop: 4 }}>⚠ {errors.slug}</div>}
            </div>
            <div>
              <FieldLabel>Descrizione (facoltativa)</FieldLabel>
              <textarea
                className="inp"
                style={{ resize: "vertical", minHeight: 72 }}
                placeholder="Chi siete, cosa producete, dove siete…"
                value={form.bio}
                onChange={(e) => setF("bio", e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Step 1: Contatti & Accesso */}
        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <FieldLabel>Email (per accedere alla piattaforma) *</FieldLabel>
              <input
                className={"inp" + (errors.email ? " err" : "")}
                type="email"
                placeholder="la-tua@email.com"
                value={form.email}
                onChange={(e) => setF("email", e.target.value)}
              />
              {errors.email && <div style={{ fontSize: 12, color: "#D04030", marginTop: 4 }}>⚠ {errors.email}</div>}
            </div>
            <div>
              <FieldLabel>Numero WhatsApp ★ — gli ordini arriveranno qui</FieldLabel>
              <PhoneInput
                value={form.waNumber}
                country={form.waCountry}
                onValueChange={(v) => setF("waNumber", v)}
                onCountryChange={(c) => setF("waCountry", c)}
                error={errors.waNumber}
              />
              {form.waNumber && selC.prefix && (
                <div style={{ fontSize: 11, color: "#9A9A90", marginTop: 4 }}>
                  Numero completo: <strong style={{ color: "#1A6B3A" }}>+{selC.prefix}{form.waNumber}</strong>
                </div>
              )}
            </div>
            <div>
              <FieldLabel>Password per il pannello Admin *</FieldLabel>
              <div style={{ position: "relative" }}>
                <input
                  className={"inp" + (errors.password ? " err" : "")}
                  type={showPw ? "text" : "password"}
                  placeholder="Minimo 6 caratteri"
                  value={form.password}
                  onChange={(e) => setF("password", e.target.value)}
                  style={{ paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#9A9A90" }}>
                  {showPw ? "🙈" : "👁"}
                </button>
              </div>
              {errors.password && <div style={{ fontSize: 12, color: "#D04030", marginTop: 4 }}>⚠ {errors.password}</div>}
            </div>
            <div>
              <FieldLabel>Conferma password *</FieldLabel>
              <div style={{ position: "relative" }}>
                <input
                  className={"inp" + (errors.confirmPassword ? " err" : "")}
                  type={showPw2 ? "text" : "password"}
                  placeholder="Ripeti la password"
                  value={form.confirmPassword}
                  onChange={(e) => setF("confirmPassword", e.target.value)}
                  style={{ paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowPw2(!showPw2)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#9A9A90" }}>
                  {showPw2 ? "🙈" : "👁"}
                </button>
              </div>
              {errors.confirmPassword && <div style={{ fontSize: 12, color: "#D04030", marginTop: 4 }}>⚠ {errors.confirmPassword}</div>}
              {form.password && form.confirmPassword && form.password === form.confirmPassword && (
                <div style={{ fontSize: 12, color: "#1A6B3A", marginTop: 4 }}>✅ Password corrispondenti</div>
              )}
            </div>
            <div style={{ background: "#FFF8EC", borderRadius: 10, padding: "12px 14px", border: "1px solid #F0D890", fontSize: 12, color: "#7A5010", lineHeight: 1.6 }}>
              ⚠️ <strong>Conserva la password.</strong> Ti serve per accedere all'admin tramite l'icona ⚙️ nel catalogo.
            </div>
          </div>
        )}

        {/* Step 2: Logistica */}
        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <FieldLabel>Info consegne (visibili ai clienti)</FieldLabel>
              <input
                className="inp"
                placeholder="Es. Consegne lun–sab, ordina entro le 18:00"
                value={form.delivery}
                onChange={(e) => setF("delivery", e.target.value)}
              />
            </div>
            <div>
              <FieldLabel>Metodi di pagamento accettati</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                {form.payments.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ flex: 1, background: "#F5F2EC", borderRadius: 8, padding: "8px 12px", fontSize: 14 }}>{p}</span>
                    <button onClick={() => removePayment(i)} style={{ color: "#A04030", fontSize: 16, padding: "2px 6px" }}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="inp"
                  value={newPm}
                  onChange={(e) => setNewPm(e.target.value)}
                  placeholder="Es. Satispay, PayPal, Carta…"
                  onKeyDown={(e) => { if (e.key === "Enter") addPayment(); }}
                  style={{ flex: 1, fontSize: 13 }}
                />
                <button
                  style={{ background: "#1A6B3A", color: "#fff", borderRadius: 8, padding: "0 14px", fontSize: 13, fontWeight: 700 }}
                  onClick={addPayment}
                >+</button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Piano */}
        {step === 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <FieldLabel>Scegli il tuo piano</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {PLATFORM.plans.map((p) => (
                  <label key={p.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderRadius: 12, border: "1.5px solid " + (form.plan === p.id ? p.color : "#E0DAD4"), background: form.plan === p.id ? "#F5FAF3" : "#fff", cursor: "pointer" }}>
                    <input type="radio" name="plan" value={p.id} checked={form.plan === p.id} onChange={() => setF("plan", p.id)} style={{ accentColor: p.color, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: p.color }}>{p.name}</span>
                        <span style={{ fontSize: 13, color: "#9A9A90" }}>{p.price === 0 ? "Gratis" : "€" + p.price + p.period}</span>
                        {p.popular && <span style={{ background: p.color, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>Consigliato</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#6B6B65", marginTop: 4, lineHeight: 1.5 }}>{p.features.slice(0, 3).join(" · ")}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel>Lingua principale — catalogo e messaggi WhatsApp</FieldLabel>
              <select className="inp" value={form.defaultLang} onChange={(e) => setF("defaultLang", e.target.value)}>
                {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <p style={{ fontSize: 11, color: "#9A9A90", marginTop: 4 }}>
                📱 I messaggi WhatsApp degli ordini arriveranno in questa lingua, indipendentemente dalla lingua scelta dal cliente nel catalogo.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        {submitError && (
          <div style={{ background: "#FEF2F2", borderRadius: 10, padding: "10px 14px", marginTop: 12, fontSize: 13, color: "#A04030", border: "1px solid #FCA5A5" }}>
            ⚠️ {submitError}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button
            style={{ background: "#F0EDE8", color: "#4A4A42", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600 }}
            onClick={step === 0 ? onBack : () => setStep((s) => s - 1)}
            disabled={submitting}
          >
            {step === 0 ? "← Indietro" : "← Precedente"}
          </button>
          <button
            style={{ flex: 1, background: "#0A2A1A", color: "#fff", borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 700, opacity: submitting ? 0.6 : 1 }}
            onClick={step === STEPS.length - 1 ? submit : () => { if (validate()) setStep((s) => s + 1); }}
            disabled={submitting}
          >
            {step === STEPS.length - 1
              ? (submitting ? "Creazione in corso..." : "✓ Crea il catalogo")
              : "Continua →"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   3. CART DRAWER
══════════════════════════════════════════════════════════════════ */
function CartDrawer({ supplier, cart, setCart, onClose, lang, onOrderPlaced }) {
  const [step, setStep] = useState("cart");
  const [savedOrder, setSavedOrder] = useState(null);
  const [od, setOD] = useState({
    name: "",
    address: "",
    phoneCountry: "IT",
    phoneNumber: "",
    payment: supplier.payments[0] || "",
    notes: "",
  });

  const allRows = supplier.tables.flatMap((t) => t.rows);
  const items = Object.entries(cart)
    .map(([id, qty]) => { const r = allRows.find((r) => r.id === id); return r ? { ...r, qty } : null; })
    .filter(Boolean);
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  const phoneC = COUNTRIES.find((c) => c.code === od.phoneCountry) || COUNTRIES[0];
  const fullPhone = "+" + phoneC.prefix + od.phoneNumber;
  const canSubmit = od.name.trim() && od.address.trim() && od.phoneNumber.trim() && od.phoneNumber.length >= 6;

  function addItem(id) {
    const row = allRows.find((r) => r.id === id);
    if (row && canAdd(row, cart[id] || 0)) {
      setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
    }
  }

  function subItem(id) {
    setCart((c) => {
      const n = { ...c };
      if (n[id] > 1) n[id]--;
      else delete n[id];
      return n;
    });
  }

  /* FIX 2.8 — rivalidazione stock al momento dell'invio
     Tra l'aggiunta al carrello e l'invio, lo stock potrebbe
     essere cambiato (admin in un'altra tab). Blocca se necessario. */
  function validateCartBeforeOrder() {
    const freshData = loadSupplier(supplier.slug);
    const freshRows = freshData.tables.flatMap((t) => t.rows);
    const invalid = [];
    items.forEach((item) => {
      const freshRow = freshRows.find((r) => r.id === item.id);
      if (!freshRow) { invalid.push(item.name + " " + tl(lang, "stockNoLongerAvail")); return; }
      if (!freshRow.on) { invalid.push(item.name + " " + tl(lang, "stockDeactivated")); return; }
      if (freshRow.stock !== null && item.qty > freshRow.stock) {
        invalid.push(item.name + ": " + tl(lang, "stockRequested") + " " + item.qty + ", " + tl(lang, "stockNowAvailable") + " " + (freshRow.stock === 0 ? "0" : freshRow.stock));
      }
    });
    return invalid;
  }

  /* FIX 2.1 + 5.1 — ordine salvato direttamente in localStorage
     (non tramite CustomEvent che si perde se AdminPanel non è aperto)
     e stock decrementato subito per tutti i piani */
  function placeOrder() {
    const stockErrors = validateCartBeforeOrder();
    if (stockErrors.length > 0) {
      alert("⚠️ " + tl(lang, "stockValidationTitle") + ":\n\n" + stockErrors.join("\n") + "\n\n" + tl(lang, "stockValidationRetry"));
      return;
    }
    const order = {
      id: "ord_" + uid(),
      timestamp: new Date().toISOString(),
      customer: {
        name: od.name,
        address: od.address,
        phone: fullPhone,
        payment: od.payment,
        notes: od.notes,
      },
      items: items.map((i) => ({
        id: i.id, name: i.name, qty: i.qty,
        unit: i.unit, price: i.price, emoji: i.emoji,
      })),
      total,
      status: "pending",
    };

    /* FIX N2/N4 — costruiamo freshData con ordine + stock decrementato,
       poi chiamiamo onOrderPlaced(freshData) che in MainApp aggiorna
       sia lo state React che il localStorage in modo atomico.
       Questo evita che il debounce del MainApp sovrascriva i dati
       dell'ordine con lo snapshot vecchio di supplier. */
    const freshData = loadSupplier(supplier.slug);
    freshData.pendingOrders = [...(freshData.pendingOrders || []), order];
    freshData.tables = freshData.tables.map((tb) => ({
      ...tb,
      rows: tb.rows.map((row) => {
        const item = order.items.find((i) => i.id === row.id);
        if (!item || row.stock === null) return row;
        return { ...row, stock: Math.max(0, (row.stock || 0) - item.qty) };
      }),
    }));
    onOrderPlaced(freshData);

    /* Salva anche su Supabase in modo asincrono (non bloccante) */
    if (supplier.supplierId) {
      dbSaveOrder(supplier.supplierId, order).catch(console.warn);
    }

    /* Notifica WhatsApp automatica al fornitore tramite Edge Function Supabase */
    if (SUPABASE_OK && supplier.whatsapp) {
      const notifyUrl = (process.env.REACT_APP_SUPABASE_URL || '')
        .replace('https://', 'https://')
        + '/functions/v1/notify-order-whatsapp';
      fetch(notifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + (process.env.REACT_APP_SUPABASE_ANON_KEY || ''),
        },
        body: JSON.stringify({
          supplier_whatsapp: supplier.whatsapp,
          supplier_name:     supplier.name,
          supplier_slug:     supplier.slug,
          supplier_lang:     supplier.defaultLang || 'it',
        }),
      }).catch(console.warn);
    }

    setSavedOrder(order);
    setStep("confirmed");
    setCart({});
  }

  function notifyWhatsApp() {
    if (!savedOrder) return;
    const supplierLang = supplier.defaultLang || "it";
    const m = WA_MESSAGES[supplierLang] || WA_MESSAGES.it;
    const lines = savedOrder.items
      .map((i) => "• " + i.qty + "× " + i.name + " (" + i.unit + ") — €" + (i.price * i.qty).toFixed(2))
      .join("\n");
    const msg = [
      m.greeting(supplier.name),
      "",
      m.intro,
      "",
      "📋 " + m.orderLabel + " #" + savedOrder.id.slice(-6).toUpperCase() + ":",
      lines,
      "",
      "💰 " + m.totalLabel + ": €" + savedOrder.total.toFixed(2),
      "💳 " + m.paymentLabel + ": " + savedOrder.customer.payment,
      "",
      "📦 " + m.deliveryLabel + ":",
      m.nameLabel + ": " + savedOrder.customer.name,
      m.addressLabel + ": " + savedOrder.customer.address,
      m.phoneLabel + ": " + savedOrder.customer.phone,
      savedOrder.customer.notes ? m.notesLabel + ": " + savedOrder.customer.notes : null,
      "",
      m.closing,
    ].filter((l) => l !== null).join("\n");
    window.open("https://wa.me/" + supplier.whatsapp + "?text=" + encodeURIComponent(msg), "_blank");
  }

  function closeAndReset() {
    onClose();
    setStep("cart");
    setSavedOrder(null);
    setOD({ name: "", address: "", phoneCountry: "IT", phoneNumber: "", payment: supplier.payments[0] || "", notes: "" });
  }

  return (
    <div
      className="fi"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.42)", zIndex: 200, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) closeAndReset(); }}
    >
      <div
        className="si"
        style={{ width: "min(460px,100vw)", background: "#F8F7F4", height: "100vh", overflowY: "auto", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #EDEAE4", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20 }}>
            {step === "confirmed" ? tl(lang, "sentTitle") : step === "checkout" ? tl(lang, "deliveryTitle") : tl(lang, "cart")}
          </h3>
          <button style={{ fontSize: 20, color: "#9A9A90", lineHeight: 1 }} onClick={closeAndReset}>✕</button>
        </div>

        {/* Drawer body */}
        <div style={{ flex: 1, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* STEP: CART */}
          {step === "cart" && items.length === 0 && (
            <div style={{ textAlign: "center", color: "#9A9A90", padding: "60px 0", flex: 1 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🧺</div>
              <p style={{ fontWeight: 600 }}>{tl(lang, "cartEmpty")}</p>
              <p style={{ fontSize: 13, marginTop: 6 }}>{tl(lang, "cartEmptySub")}</p>
            </div>
          )}

          {step === "cart" && items.length > 0 && (
            <>
              {items.map((item) => {
                const row = allRows.find((r) => r.id === item.id);
                const maxReached = row && !canAdd(row, item.qty);
                return (
                  <div key={item.id} style={{ background: "#fff", borderRadius: 12, padding: "12px 14px", border: "1px solid #EDEAE4", display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{item.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: "#9A9A90" }}>€{item.price.toFixed(2)}/{item.unit}</div>
                      {maxReached && (
                        <div style={{ fontSize: 10, color: "#C05020", fontWeight: 600, marginTop: 2 }}>
                          ⚠️ {tl(lang, "maxReached")} ({row.stock} {row.unit})
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        style={{ background: "#EDEAE4", borderRadius: 8, width: 40, height: 40, fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={() => subItem(item.id)}
                      >−</button>
                      <span style={{ fontWeight: 700, fontSize: 14, minWidth: 18, textAlign: "center" }}>{item.qty}</span>
                      <button
                        style={{ background: "#EDEAE4", borderRadius: 8, width: 40, height: 40, fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", opacity: maxReached ? .45 : 1, cursor: maxReached ? "not-allowed" : "pointer" }}
                        onClick={() => addItem(item.id)}
                        disabled={maxReached}
                      >+</button>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#C07B54", minWidth: 50, textAlign: "right" }}>
                      €{(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>
                );
              })}
              <div style={{ borderTop: "2px dashed #E0DAD4", paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 600 }}>{tl(lang, "total")}</span>
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700, color: "#C07B54" }}>€{total.toFixed(2)}</span>
              </div>
              <button
                style={{ background: "#0A2A1A", color: "#fff", borderRadius: 11, padding: 14, fontSize: 14, fontWeight: 700 }}
                onClick={() => setStep("checkout")}
              >
                {tl(lang, "proceed")}
              </button>
            </>
          )}

          {/* STEP: CHECKOUT */}
          {step === "checkout" && (
            <div className="fu">
              <div style={{ marginBottom: 14 }}>
                <FieldLabel>{tl(lang, "yourName")}</FieldLabel>
                <input className="inp" placeholder={tl(lang, "namePh")} value={od.name} onChange={(e) => setOD((d) => ({ ...d, name: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <FieldLabel>{tl(lang, "address")}</FieldLabel>
                <input className="inp" placeholder={tl(lang, "addressPh")} value={od.address} onChange={(e) => setOD((d) => ({ ...d, address: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <FieldLabel>{tl(lang, "phone")}</FieldLabel>
                <PhoneInput
                  value={od.phoneNumber}
                  country={od.phoneCountry}
                  placeholder={tl(lang, "phonePh")}
                  onValueChange={(v) => setOD((d) => ({ ...d, phoneNumber: v }))}
                  onCountryChange={(c) => setOD((d) => ({ ...d, phoneCountry: c }))}
                />
                {od.phoneNumber && od.phoneNumber.length >= 6 && (
                  <div style={{ fontSize: 11, color: "#9A9A90", marginTop: 4 }}>
                    Il fornitore potrà contattarti al <strong style={{ color: "#1A6B3A" }}>{fullPhone}</strong>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 14 }}>
                <FieldLabel>{tl(lang, "payment")}</FieldLabel>
                <select className="inp" value={od.payment} onChange={(e) => setOD((d) => ({ ...d, payment: e.target.value }))}>
                  {supplier.payments.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <FieldLabel>{tl(lang, "notes")}</FieldLabel>
                <textarea
                  className="inp"
                  style={{ resize: "vertical", minHeight: 68 }}
                  placeholder={tl(lang, "notesPh")}
                  value={od.notes}
                  onChange={(e) => setOD((d) => ({ ...d, notes: e.target.value }))}
                />
              </div>
              <div style={{ background: "#EDEAE4", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: "#6B6B65" }}>
                <strong style={{ color: "#1C1C1A" }}>{tl(lang, "itemsCount", { n: count })}</strong> · €{total.toFixed(2)} · {od.payment}
              </div>
              <button
                style={{ background: "#0A2A1A", color: "#fff", borderRadius: 11, padding: 14, fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: canSubmit ? 1 : .5, cursor: canSubmit ? "pointer" : "not-allowed", width: "100%", marginBottom: 10 }}
                onClick={placeOrder}
                disabled={!canSubmit}
              >
                ✅ {tl(lang, "sendOrder")}
              </button>
              {!canSubmit && (
                <p style={{ fontSize: 11, color: "#9A9A90", textAlign: "center" }}>{tl(lang, "formRequired")}</p>
              )}
              <button
                style={{ fontSize: 13, color: "#9A9A90", textAlign: "center", width: "100%", marginTop: 4 }}
                onClick={() => setStep("cart")}
              >
                {tl(lang, "editOrder")}
              </button>
            </div>
          )}

          {/* STEP: CONFIRMED + WA BANNER */}
          {step === "confirmed" && savedOrder && (
            <div className="fu">
              <div className="bi" style={{ textAlign: "center", padding: "28px 16px", background: "#E4F4E8", borderRadius: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
                <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "#1A5A2A", marginBottom: 8 }}>
                  {tl(lang, "sentTitle")}
                </h3>
                <p style={{ fontSize: 13, color: "#2A7A3A", lineHeight: 1.6 }}>
                  {tl(lang, "orderRegistered")} · <strong>#{savedOrder.id.slice(-6).toUpperCase()}</strong> · €{savedOrder.total.toFixed(2)}
                </p>
              </div>

              <div className="wa-banner">
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                  <span style={{ fontSize: 28, flexShrink: 0 }}>📲</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#E0F8E8", marginBottom: 4 }}>
                      {tl(lang, "waBannerTitle")}
                    </div>
                    <p style={{ fontSize: 13, color: "#90C8A0", lineHeight: 1.6 }}>
                      {tl(lang, "waBannerText")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={notifyWhatsApp}
                  style={{ width: "100%", background: "#25D366", color: "#fff", borderRadius: 10, padding: "13px", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                >
                  {tl(lang, "waNotifyBtn")}
                </button>
              </div>

              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #EDEAE4", overflow: "hidden", marginTop: 4 }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #F0EDE8", fontSize: 13, fontWeight: 600, color: "#6B6B65" }}>
                  {tl(lang, "orderSummary")}
                </div>
                <div style={{ padding: "8px 16px" }}>
                  {savedOrder.items.map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13, borderBottom: i < savedOrder.items.length - 1 ? "1px solid #F5F2EE" : "none" }}>
                      <span>{item.emoji} {item.qty}× {item.name}</span>
                      <span style={{ fontWeight: 600, color: "#C07B54" }}>€{(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div style={{ padding: "10px 16px", borderTop: "1.5px solid #EDEAE4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{tl(lang, "total")}</span>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 700, color: "#C07B54" }}>€{savedOrder.total.toFixed(2)}</span>
                </div>
              </div>

              <button
                style={{ marginTop: 8, fontSize: 13, color: "#9A9A90", textAlign: "center", width: "100%", padding: 8 }}
                onClick={closeAndReset}
              >
                {tl(lang, "backToCatalog")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   4. SUPPLIER CATALOG
══════════════════════════════════════════════════════════════════ */
function SupplierCatalog({ supplier, cart, setCart, onAdmin, onHome, onOrderPlaced }) {
  const [showCart, setShowCart] = useState(false);
  const [lang, setLang] = useState(supplier.defaultLang || "it");
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(null);
  const isPro = supplier.plan === "pro";

  /* FIX 3.4 — chiudi menu lingua cliccando fuori */
  const langMenuRef = useRef(null);
  useEffect(() => {
    function handleOutside(e) {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const cartCount = Object.values(cart).reduce((s, v) => s + v, 0);

  function addItem(row) {
    if (canAdd(row, cart[row.id] || 0)) {
      setCart((c) => ({ ...c, [row.id]: (c[row.id] || 0) + 1 }));
    }
  }

  function subItem(id) {
    setCart((c) => {
      const n = { ...c };
      if (n[id] > 1) n[id]--;
      else delete n[id];
      return n;
    });
  }

  async function switchLang(newLang) {
    setLang(newLang);
    setShowLangMenu(false);
    if (newLang === (supplier.defaultLang || "it") || newLang === "it") {
      setTranslated(null);
      return;
    }
    if (!isPro) return;
    setTranslating(true);
    try {
      const result = await translateCatalog(supplier.tables, newLang);
      const merged = supplier.tables.map((tb) => {
        const tr = result.find((r) => r.id === tb.id);
        return {
          ...tb,
          name: tr ? tr.tableName : tb.name,
          rows: tb.rows.map((row) => {
            const tr2 = tr ? tr.rows.find((r) => r.id === row.id) : null;
            return { ...row, name: tr2 ? tr2.name : row.name, desc: tr2 ? tr2.desc : row.desc };
          }),
        };
      });
      setTranslated(merged);
    } catch {
      setTranslated(null);
    } finally {
      setTranslating(false);
    }
  }

  const displayTables = translated || supplier.tables;

  return (
    <div style={{ minHeight: "100vh", background: "#F8F7F4" }}>
      {/* Header */}
      <header style={{ background: "#fff", borderBottom: "1px solid #EDEAE4", padding: "16px 24px 14px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <button onClick={onHome} style={{ fontSize: 11, color: "#9A9A90", marginBottom: 5, letterSpacing: ".04em" }}>
            {tl(lang, "backTo")}
          </button>
          <h1 style={{ fontSize: "clamp(20px,4vw,26px)", fontWeight: 700, color: "#0A1A0A", lineHeight: 1.2 }}>
            {supplier.name}
          </h1>
          <p style={{ fontSize: 13, color: "#6B6B65", marginTop: 4, lineHeight: 1.55, maxWidth: 460 }}>
            {supplier.bio}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
          {/* Language switcher — FIX 3.4: ref per click-outside */}
          <div ref={langMenuRef} style={{ position: "relative" }}>
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              style={{ background: "#F0EDE8", borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: 500, color: "#4A4A42", display: "flex", alignItems: "center", gap: 5 }}
            >
              {LANGS[lang]?.split(" ")[0]} {lang.toUpperCase()} ▾
            </button>
            {showLangMenu && (
              <div style={{ position: "absolute", top: 40, right: 0, background: "#fff", borderRadius: 12, border: "1px solid #EDEAE4", boxShadow: "0 4px 20px rgba(0,0,0,.1)", padding: 8, zIndex: 100, minWidth: 180 }}>
                {Object.entries(LANGS).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => switchLang(k)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13, color: "#1C1C1A", background: lang === k ? "#F0F8F0" : "transparent" }}
                  >
                    <span>{v}</span>
                    {!isPro && k !== (supplier.defaultLang || "it") && k !== "it" && (
                      <span style={{ fontSize: 10, background: "#1A3A8F", color: "#fff", borderRadius: 6, padding: "2px 6px", fontWeight: 700 }}>PRO</span>
                    )}
                    {lang === k && <span style={{ color: "#1A6B3A" }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Admin gear */}
          <button
            onClick={onAdmin}
            style={{ background: "#F0EDE8", borderRadius: 50, width: 36, height: 36, fontSize: 15, color: "#9A9A90", display: "flex", alignItems: "center", justifyContent: "center" }}
          >⚙️</button>
          {/* Cart button */}
          <button
            onClick={() => setShowCart(true)}
            style={{ background: "#0A2A1A", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 6 }}
          >
            🛒
            {cartCount > 0 && (
              <span style={{ background: "#5CC870", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 700, padding: "1px 6px", minWidth: 18, textAlign: "center" }}>
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Translating banner */}
      {translating && (
        <div style={{ background: "#EEF4FF", borderBottom: "1px solid #C8D8F0", padding: "8px 24px", fontSize: 12, color: "#1A3A8F", display: "flex", alignItems: "center", gap: 8 }}>
          <Spinner /> {tl(lang, "translating")}
        </div>
      )}

      {/* Delivery banner */}
      <div style={{ background: "#E4F4E8", borderBottom: "1px solid #C4E4C8", padding: "8px 24px", fontSize: 12, color: "#1A6B3A" }}>
        {tl(lang, "delivery")} {supplier.delivery}
      </div>

      {/* Product tables */}
      <main style={{ maxWidth: 920, margin: "0 auto", padding: "24px 16px 70px" }}>
        {displayTables.map((table, ti) => (
          <section key={table.id} className="fu" style={{ animationDelay: ti * .08 + "s", marginBottom: 36 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0A1A0A" }}>{table.name}</h2>
              <div style={{ flex: 1, height: 1, background: "#EDEAE4" }} />
              <span style={{ fontSize: 12, color: "#9A9A90" }}>
                {tl(lang, "availCount", { n: table.rows.filter((r) => r.on && (r.stock === null || r.stock > 0)).length })}
              </span>
            </div>
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #EDEAE4", overflow: "hidden" }}>
              <table className="pt">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <th>{tl(lang, "colProduct")}</th>
                    <th>{tl(lang, "colPrice")}</th>
                    <th>{tl(lang, "colAvail")}</th>
                    <th style={{ textAlign: "right" }}>{tl(lang, "colQty")}</th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => {
                    const qty = cart[row.id] || 0;
                    const ok = canAdd(row, qty);
                    const unavail = !row.on || (row.stock !== null && row.stock <= 0);
                    const sl = getStockLabel(row, lang);
                    return (
                      <tr key={row.id} className={unavail ? "off" : ""}>
                        <td style={{ fontSize: 22 }}>{row.emoji}</td>
                        <td className="td-name">
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</div>
                          <div style={{ fontSize: 12, color: "#9A9A90", marginTop: 2 }}>{row.desc}</div>
                        </td>
                        <td style={{ fontWeight: 700, color: "#C07B54", whiteSpace: "nowrap" }}>
                          €{row.price.toFixed(2)}
                          <span style={{ fontWeight: 400, fontSize: 11, color: "#B0A898" }}>/{row.unit}</span>
                        </td>
                        <td>
                          {unavail ? (
                            <AvailBadge row={row} lang={lang} />
                          ) : sl ? (
                            <div>
                              <AvailBadge row={row} lang={lang} />
                              <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: sl.urgent ? "#C05020" : "#1A6B3A" }}>
                                {sl.urgent && "⚠️ "}{sl.text}
                              </div>
                            </div>
                          ) : (
                            <AvailBadge row={row} lang={lang} />
                          )}
                        </td>
                        <td className="td-action" style={{ textAlign: "right" }}>
                          {unavail ? (
                            <span style={{ fontSize: 12, color: "#9A9A90" }}>—</span>
                          ) : qty === 0 ? (
                            <button
                              style={{ background: "#0A2A1A", color: "#fff", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 700 }}
                              onClick={() => addItem(row)}
                            >
                              {tl(lang, "addToCart")}
                            </button>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                              <button
                                style={{ background: "#EDEAE4", borderRadius: 8, width: 40, height: 40, fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
                                onClick={() => subItem(row.id)}
                              >−</button>
                              <span style={{ fontWeight: 700, minWidth: 20, textAlign: "center" }}>{qty}</span>
                              <button
                                style={{ background: ok ? "#EDEAE4" : "#F0EDE8", borderRadius: 8, width: 40, height: 40, fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", opacity: ok ? 1 : .45, cursor: ok ? "pointer" : "not-allowed" }}
                                onClick={() => addItem(row)}
                                disabled={!ok}
                              >+</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </main>

      {showCart && (
        <CartDrawer supplier={supplier} cart={cart} setCart={setCart} onClose={() => setShowCart(false)} lang={lang} onOrderPlaced={onOrderPlaced} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   5. ADMIN LOGIN
══════════════════════════════════════════════════════════════════ */
function AdminLogin({ supplier, onSuccess, onBack }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  async function login() {
    if (!pw) return;
    setLoading(true);
    setErr('');
    try {
      /* Se Supabase è configurato, prova Auth cloud */
      if (SUPABASE_OK) {
        try {
          const user = await dbLoginSupplier(supplier.email, pw);
          if (user) { onSuccess(user.id); return; }
        } catch {
          /* Se Supabase fallisce, prova il fallback locale */
        }
      }
      /* Fallback: password salvata in localStorage */
      if (pw === supplier.password) {
        onSuccess(supplier.supplierId || null);
      } else {
        setErr("Password non corretta ❌");
        setPw("");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#EDEAE4", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="pi" style={{ background: "#fff", borderRadius: 20, padding: 36, maxWidth: 380, width: "100%", border: "1px solid #EDEAE4", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 14 }}>🔐</div>
        <h2 style={{ fontSize: 24, marginBottom: 6 }}>Pannello Admin</h2>
        <p style={{ fontSize: 13, color: "#6B6B65", marginBottom: 22 }}>{supplier.name}</p>
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input
            className="inp"
            type={show ? "text" : "password"}
            placeholder="Password"
            value={pw}
            onChange={(e) => { setPw(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && login()}
            style={{ textAlign: "center", paddingRight: 44, fontSize: 15 }}
          />
          <button type="button" onClick={() => setShow(!show)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#9A9A90" }}>
            {show ? "🙈" : "👁"}
          </button>
        </div>
        {err && <p style={{ color: "#A04030", fontSize: 13, marginBottom: 8 }}>{err}</p>}
        <button
          style={{ width: "100%", padding: 12, borderRadius: 10, background: "#0A2A1A", color: "#fff", fontSize: 14, fontWeight: 700, marginBottom: 10, opacity: loading ? 0.6 : 1 }}
          onClick={login}
          disabled={loading}
        >
          {loading ? "Accesso in corso..." : "Accedi"}
        </button>
        <button style={{ fontSize: 13, color: "#9A9A90" }} onClick={onBack}>← Torna al catalogo</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   6. ADMIN PANEL
══════════════════════════════════════════════════════════════════ */
function AdminPanel({ supplier, setSupplier, onBack, supplierId }) {
  const [tab, setTab] = useState("products");
  const [addRowFor, setAddRowFor] = useState(null);
  const [newRow, setNewRow] = useState(emptyRow());
  const [addTableName, setAddTableName] = useState("");
  const [showAddTable, setShowAddTable] = useState(false);
  const [editTableId, setEditTableId] = useState(null);
  const [editTableName, setEditTableName] = useState("");
  const [newPm, setNewPm] = useState("");
  const [showPwSection, setShowPwSection] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");

  const isPro = supplier.plan === "pro";

  function emptyRow() {
    return { emoji: "📦", name: "", desc: "", price: "", unit: "", stock: "", on: true };
  }

  /* Fix #6 Opus — update() non chiama più dbUpdateSupplierProfile ad ogni click
     (toggle stock, conferma ordine, ecc. generavano chiamate inutili a Supabase).
     La sync del profilo avviene solo esplicitamente dal tab Impostazioni. */
  const update = useCallback((fn) => {
    setSupplier((s) => {
      const n = fn(s);
      saveSupplier(n);
      return n;
    });
  }, [setSupplier]);

  /* Sincronizza solo i campi del profilo su Supabase — chiamata esplicitamente
     solo quando l'utente salva una modifica nel tab Impostazioni */
  function syncProfileToSupabase(n) {
    if (!supplierId) return;
    dbUpdateSupplierProfile(supplierId, {
      name: n.name,
      bio: n.bio,
      delivery: n.delivery,
      payments: n.payments,
      wa_country: n.waCountry,
      wa_number: n.waNumber,
      whatsapp: n.whatsapp,
      default_lang: n.defaultLang,
      plan: n.plan,
    }).catch(console.warn);
  }

  /* Carica ordini da Supabase quando il pannello si apre.
     Fix #2 Opus: Supabase è la fonte di verità per lo STATUS degli ordini.
     Gli ordini già presenti in localStorage vengono aggiornati con lo
     status di Supabase (es. confermato su altro dispositivo → si aggiorna). */
  useEffect(() => {
    if (!supplierId) return;
    dbLoadOrders(supplierId).then((dbOrders) => {
      if (dbOrders.length === 0) return;
      setSupplier((s) => {
        const localOrders = s.pendingOrders || [];
        const localMap = new Map(localOrders.map((o) => [o.id, o]));
        const dbMap = new Map(dbOrders.map((o) => [o.id, o]));

        /* Aggiorna status degli ordini esistenti con il valore da Supabase */
        const updatedLocal = localOrders.map((o) => {
          const dbOrder = dbMap.get(o.id);
          if (dbOrder && dbOrder.status !== o.status) {
            return { ...o, status: dbOrder.status };
          }
          return o;
        });

        /* Aggiungi ordini presenti solo su Supabase (arrivati da altro device) */
        const newOrders = dbOrders.filter((o) => !localMap.has(o.id));

        const merged = [...updatedLocal, ...newOrders].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        /* Salva solo se c'è qualcosa di cambiato */
        const hasChanges = newOrders.length > 0 ||
          updatedLocal.some((o, i) => o.status !== localOrders[i]?.status);
        if (!hasChanges) return s;

        const updated = { ...s, pendingOrders: merged };
        saveSupplier(updated);
        return updated;
      });
    }).catch(console.warn);
  }, [supplierId]);

  function toggleRow(tid, rid) {
    update((s) => ({
      ...s,
      tables: s.tables.map((t) =>
        t.id === tid ? { ...t, rows: t.rows.map((r) => r.id === rid ? { ...r, on: !r.on } : r) } : t
      ),
    }));
  }

  function delRow(tid, rid) {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    update((s) => ({
      ...s,
      tables: s.tables.map((t) =>
        t.id === tid ? { ...t, rows: t.rows.filter((r) => r.id !== rid) } : t
      ),
    }));
  }

  function saveField(tid, rid, field, raw) {
    let val = raw;
    if (field === "price") val = parseFloat(String(raw).replace(/[^0-9.,]/g, "").replace(",", ".")) || 0;
    if (field === "stock") val = (raw === "" || raw === null) ? null : Math.max(0, parseFloat(raw)) || 0;
    update((s) => ({
      ...s,
      tables: s.tables.map((t) =>
        t.id === tid ? { ...t, rows: t.rows.map((r) => r.id === rid ? { ...r, [field]: val } : r) } : t
      ),
    }));
  }

  /* FIX 2.7 — usa closure update() per leggere dati freschi
     (evita stale data con click rapidi su +/−) */
  function adjustStock(tid, rid, delta) {
    update((s) => {
      const row = s.tables.find((t) => t.id === tid)?.rows.find((r) => r.id === rid);
      if (!row) return s;
      const cur = row.stock === null ? 0 : row.stock;
      const newStock = Math.max(0, cur + delta);
      return {
        ...s,
        tables: s.tables.map((t) =>
          t.id === tid
            ? { ...t, rows: t.rows.map((r) => r.id === rid ? { ...r, stock: newStock } : r) }
            : t
        ),
      };
    });
  }

  function addRow(tid) {
    if (!newRow.name || !newRow.price) return;
    /* FIX 5.2 — blocca oltre 15 prodotti per piano Starter */
    if (supplier.plan === "starter") {
      const totalProducts = supplier.tables.flatMap((t) => t.rows).length;
      if (totalProducts >= 15) {
        alert("Piano Starter: hai raggiunto il limite di 15 prodotti.\nPassa al piano Base per aggiungerne altri.");
        return;
      }
    }
    const row = {
      ...newRow,
      id: "r_" + uid(),
      price: parseFloat(newRow.price) || 0,
      stock: (newRow.stock === "" || newRow.stock === null) ? null : parseFloat(newRow.stock) ?? null,
    };
    update((s) => ({
      ...s,
      tables: s.tables.map((t) => t.id === tid ? { ...t, rows: [...t.rows, row] } : t),
    }));
    setNewRow(emptyRow());
    setAddRowFor(null);
  }

  function addTable() {
    if (!addTableName.trim()) return;
    /* FIX 5.3 — blocca oltre 1 sezione per piano Starter */
    if (supplier.plan === "starter" && supplier.tables.length >= 1) {
      alert("Piano Starter: puoi avere solo 1 sezione.\nPassa al piano Base per sezioni illimitate.");
      return;
    }
    update((s) => ({ ...s, tables: [...s.tables, { id: "t_" + uid(), name: addTableName.trim(), rows: [] }] }));
    setAddTableName("");
    setShowAddTable(false);
  }

  function renameTable(tid) {
    if (!editTableName.trim()) return;
    update((s) => ({
      ...s,
      tables: s.tables.map((t) => t.id === tid ? { ...t, name: editTableName.trim() } : t),
    }));
    setEditTableId(null);
  }

  function delTable(tid) {
    if (!window.confirm("Eliminare tutta questa sezione?")) return;
    update((s) => ({ ...s, tables: s.tables.filter((t) => t.id !== tid) }));
  }

  function changePw() {
    if (pwForm.current !== supplier.password) { setPwErr("Password attuale non corretta"); return; }
    if (pwForm.next.length < 6) { setPwErr("Minimo 6 caratteri"); return; }
    if (pwForm.next !== pwForm.confirm) { setPwErr("Non corrispondono"); return; }
    update((s) => ({ ...s, password: pwForm.next }));
    /* Fix #5 Opus — aggiorna anche la password su Supabase Auth
       così il prossimo login con la nuova password funziona correttamente */
    supabase.auth.updateUser({ password: pwForm.next }).catch(console.warn);
    setPwForm({ current: "", next: "", confirm: "" });
    setShowPwSection(false);
    setPwErr("");
  }

  /* FIX 5.4 — avvisa se lo stock è insufficiente per l'ordine da confermare */
  /* FIX N1 — confirmOrder NON decrementa lo stock.
     Lo stock è già stato decrementato da placeOrder al momento dell'ordine
     (FIX 5.1). Qui cambiamo solo lo status da "pending" a "confirmed".
     Decrementare di nuovo causerebbe un doppio taglio dello stock. */
  function confirmOrder(ordId) {
    update((s) => {
      const ord = (s.pendingOrders || []).find((o) => o.id === ordId);
      if (!ord) return s;
      return {
        ...s,
        pendingOrders: (s.pendingOrders || []).map((o) =>
          o.id === ordId ? { ...o, status: "confirmed" } : o
        ),
      };
    });
    /* Sync su Supabase */
    dbUpdateOrderStatus(ordId, "confirmed").catch(console.warn);
  }

  function dismissOrder(id) {
    update((s) => ({ ...s, pendingOrders: (s.pendingOrders || []).filter((o) => o.id !== id) }));
    /* Sync su Supabase */
    dbUpdateOrderStatus(id, "dismissed").catch(console.warn);
  }

  const pending   = (supplier.pendingOrders || []).filter((o) => o.status === "pending");
  const confirmed = (supplier.pendingOrders || []).filter((o) => o.status === "confirmed");

  const totals = {
    sections: supplier.tables.length,
    products: supplier.tables.flatMap((t) => t.rows).length,
    avail:    supplier.tables.flatMap((t) => t.rows).filter((r) => r.on && (r.stock === null || r.stock > 0)).length,
    off:      supplier.tables.flatMap((t) => t.rows).filter((r) => !r.on || (r.stock !== null && r.stock <= 0)).length,
  };

  return (
    <div style={{ minHeight: "100vh", background: "#EDEAE4" }}>
      {/* Header */}
      <header style={{ background: "linear-gradient(135deg,#0A2A1A,#1A5A2A)", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 700, color: "#EFF8F0" }}>⚙️ Admin Panel</div>
          <div style={{ fontSize: 12, color: "#80C890", marginTop: 2 }}>{supplier.name} · Piano {supplier.plan.toUpperCase()}</div>
        </div>
        <button style={{ background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.2)", color: "#C0E8C8", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600 }} onClick={onBack}>
          ← Catalogo
        </button>
      </header>

      <div style={{ maxWidth: 960, margin: "20px auto 0", padding: "0 16px 60px" }}>
        {/* KPI cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 18 }}>
          {[
            ["Sezioni",    totals.sections, "#1C1C1A"],
            ["Prodotti",   totals.products, "#1C1C1A"],
            ["Disponibili",totals.avail,    "#1A6B3A"],
            ["Non disp.",  totals.off,      "#A04030"],
            ["In attesa",  pending.length,  isPro ? "#C07B30" : "#9A9A90"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", border: "1px solid #DDDAD4", position: "relative", overflow: "hidden" }}>
              <div style={{ fontSize: 11, color: "#9A9A90", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>{l}</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
              {l === "In attesa" && !isPro && (
                <div style={{ position: "absolute", top: 8, right: 8, fontSize: 9, background: "#1A3A8F", color: "#fff", borderRadius: 6, padding: "2px 6px", fontWeight: 700 }}>PRO</div>
              )}
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, background: "#DDDAD4", borderRadius: 12, padding: 4, marginBottom: 20, overflowX: "auto" }}>
          {[
            ["products", "📦 Prodotti"],
            ["settings", "⚙️ Impostazioni"],
            ["orders",   "📬 Ordini" + (pending.length > 0 ? " (" + pending.length + ")" : "")],
          ].map(([id, label]) => (
            <button key={id} className={"tab-btn" + (tab === id ? " active" : "")} onClick={() => setTab(id)}>
              {label}
              {id === "orders" && !isPro && (
                <span style={{ fontSize: 9, background: "#1A3A8F", color: "#fff", borderRadius: 6, padding: "1px 5px", fontWeight: 700, marginLeft: 5 }}>PRO</span>
              )}
            </button>
          ))}
        </div>

        {/* ─── TAB: PRODOTTI ─── */}
        {tab === "products" && (
          <>
            <div style={{ background: "#fff", borderRadius: 10, padding: "10px 16px", marginBottom: 16, fontSize: 12, color: "#6B6B65", border: "1px solid #DDDAD4", display: "flex", alignItems: "center", gap: 8 }}>
              <span>💡</span>
              <span><strong>Doppio click</strong> su nome, descrizione, prezzo o unità per modificare. Per lo <strong>stock</strong> usa ＋/－ o il campo diretto.</span>
            </div>

            {supplier.tables.map((table, ti) => (
              <section key={table.id} className="fu" style={{ animationDelay: ti * .04 + "s", background: "#fff", borderRadius: 16, border: "1px solid #DDDAD4", marginBottom: 14, overflow: "hidden" }}>
                {/* Table header */}
                <div style={{ padding: "14px 18px", borderBottom: "1px solid #F0EDE8", display: "flex", alignItems: "center", gap: 10 }}>
                  {editTableId === table.id ? (
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flex: 1 }}>
                      <input
                        className="inp"
                        value={editTableName}
                        onChange={(e) => setEditTableName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") renameTable(table.id); if (e.key === "Escape") setEditTableId(null); }}
                        style={{ maxWidth: 220, fontSize: 15, fontWeight: 600 }}
                      />
                      <button style={{ background: "#1A6B3A", color: "#fff", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 700 }} onClick={() => renameTable(table.id)}>✓</button>
                      <button style={{ background: "#F0EDE8", color: "#6B6B65", borderRadius: 7, padding: "5px 10px", fontSize: 12 }} onClick={() => setEditTableId(null)}>✕</button>
                    </span>
                  ) : (
                    <>
                      <h3 style={{ flex: 1, fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 700 }}>{table.name}</h3>
                      <span style={{ fontSize: 12, color: "#9A9A90" }}>{table.rows.length} prodotti</span>
                      <button style={{ background: "#F0EDE8", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 500, color: "#4A4A42" }} onClick={() => { setEditTableId(table.id); setEditTableName(table.name); }}>✏️ Rinomina</button>
                      <button style={{ background: "#FEF2F2", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 500, color: "#A04030" }} onClick={() => delTable(table.id)}>🗑️</button>
                    </>
                  )}
                </div>

                {/* Product rows */}
                <div style={{ overflowX: "auto" }}>
                  <table className="pt" style={{ minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Nome</th>
                        <th>Descrizione</th>
                        <th>Prezzo</th>
                        <th>Unità <span style={{ fontSize: 9, fontWeight: 400, color: "#B0A898" }}>(libera)</span></th>
                        <th>Stock <span style={{ fontSize: 9, fontWeight: 400, color: "#B0A898" }}>(vuoto=∞)</span></th>
                        <th style={{ textAlign: "center" }}>On</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row) => (
                        <tr key={row.id} className={!row.on ? "off" : ""}>
                          <td style={{ fontSize: 20 }}>{row.emoji}</td>
                          <td style={{ minWidth: 110 }}>
                            <InlineEdit value={row.name} width="140px" onSave={(v) => saveField(table.id, row.id, "name", v)} />
                          </td>
                          <td style={{ minWidth: 130, fontSize: 12, color: "#6B6B65" }}>
                            <InlineEdit value={row.desc} width="170px" onSave={(v) => saveField(table.id, row.id, "desc", v)} />
                          </td>
                          <td style={{ fontWeight: 700, color: "#C07B54", whiteSpace: "nowrap" }}>
                            {/* FIX 2.5 — passa solo il numero, non "€3.50" */}
                            <span style={{ fontSize: 12, color: "#9A9A90", marginRight: 2 }}>€</span>
                            <InlineEdit value={row.price.toFixed(2)} type="number" width="70px" onSave={(v) => saveField(table.id, row.id, "price", v)} />
                          </td>
                          <td>
                            <InlineEdit value={row.unit} width="100px" placeholder="kg, litri, busta…" onSave={(v) => saveField(table.id, row.id, "unit", v)} />
                          </td>
                          <td style={{ minWidth: 190 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <StockBar row={row} />
                              <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                                <button
                                  style={{ background: "#F0EDE8", borderRadius: 5, width: 24, height: 24, fontSize: 13, fontWeight: 700, color: (row.stock === null || row.stock <= 0) ? "#C0B8A8" : "#4A4A42", display: "flex", alignItems: "center", justifyContent: "center" }}
                                  onClick={() => adjustStock(table.id, row.id, -1)}
                                  disabled={row.stock === null || row.stock <= 0}
                                >−</button>
                                <input
                                  type="number" step="0.5" min="0"
                                  value={row.stock === null ? "" : row.stock}
                                  placeholder="∞"
                                  onChange={(e) => saveField(table.id, row.id, "stock", e.target.value === "" ? null : e.target.value)}
                                  style={{ width: 54, padding: "3px 6px", border: "1.5px solid #E0DAD4", borderRadius: 6, fontSize: 13, textAlign: "center", fontFamily: "'Outfit', sans-serif" }}
                                />
                                <button
                                  style={{ background: "#E4F4E8", borderRadius: 5, width: 24, height: 24, fontSize: 13, fontWeight: 700, color: "#1A6B3A", display: "flex", alignItems: "center", justifyContent: "center" }}
                                  onClick={() => adjustStock(table.id, row.id, 1)}
                                >+</button>
                                {row.stock !== null && (
                                  <button
                                    title="Imposta illimitato"
                                    style={{ background: "#F0EDE8", borderRadius: 5, padding: "2px 6px", fontSize: 10, color: "#9A9A90" }}
                                    onClick={() => saveField(table.id, row.id, "stock", null)}
                                  >∞</button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <label className="tgl">
                              <input type="checkbox" checked={row.on} onChange={() => toggleRow(table.id, row.id)} />
                              <span className="tgl-sl" />
                            </label>
                          </td>
                          <td>
                            <button style={{ color: "#C0A898", fontSize: 15, padding: "2px 6px" }} onClick={() => delRow(table.id, row.id)}>🗑️</button>
                          </td>
                        </tr>
                      ))}

                      {/* Add row form */}
                      {addRowFor === table.id && (
                        <tr style={{ background: "#F5FAF3" }}>
                          <td>
                            <input value={newRow.emoji} onChange={(e) => setNewRow((r) => ({ ...r, emoji: e.target.value }))} style={{ width: 40, padding: 4, border: "1.5px solid #C0E0C0", borderRadius: 6, fontSize: 18, textAlign: "center", fontFamily: "'Outfit', sans-serif" }} />
                          </td>
                          <td><input className="inp" value={newRow.name} onChange={(e) => setNewRow((r) => ({ ...r, name: e.target.value }))} placeholder="Nome *" style={{ fontSize: 13 }} /></td>
                          <td><input className="inp" value={newRow.desc} onChange={(e) => setNewRow((r) => ({ ...r, desc: e.target.value }))} placeholder="Descrizione" style={{ fontSize: 13 }} /></td>
                          <td><input className="inp" type="number" step="0.01" value={newRow.price} onChange={(e) => setNewRow((r) => ({ ...r, price: e.target.value }))} placeholder="0.00 *" style={{ fontSize: 13, width: 80 }} /></td>
                          <td><input className="inp" value={newRow.unit} onChange={(e) => setNewRow((r) => ({ ...r, unit: e.target.value }))} placeholder="kg, litri, busta…" style={{ fontSize: 13, width: 110 }} /></td>
                          <td><input className="inp" type="number" step="0.5" min="0" value={newRow.stock} onChange={(e) => setNewRow((r) => ({ ...r, stock: e.target.value }))} placeholder="vuoto = ∞" style={{ fontSize: 13, width: 130 }} /></td>
                          <td style={{ textAlign: "center" }}>
                            <label className="tgl">
                              <input type="checkbox" checked={newRow.on} onChange={(e) => setNewRow((r) => ({ ...r, on: e.target.checked }))} />
                              <span className="tgl-sl" />
                            </label>
                          </td>
                          <td>
                            <button style={{ background: "#1A6B3A", color: "#fff", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 700, marginRight: 4 }} onClick={() => addRow(table.id)}>✓</button>
                            <button style={{ background: "#F0EDE8", color: "#6B6B65", borderRadius: 7, padding: "5px 10px", fontSize: 12 }} onClick={() => setAddRowFor(null)}>✕</button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {addRowFor !== table.id && (
                  <div style={{ padding: "10px 16px", borderTop: "1px solid #F0EDE8" }}>
                    <button
                      style={{ background: "none", border: "1.5px dashed #C0B8A8", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 600, color: "#6B6B65", width: "100%" }}
                      onClick={() => { setAddRowFor(table.id); setNewRow(emptyRow()); }}
                    >+ Aggiungi prodotto</button>
                  </div>
                )}
              </section>
            ))}

            {showAddTable ? (
              <div className="fu" style={{ background: "#fff", borderRadius: 14, border: "2px solid #1A6B3A", padding: 20, marginBottom: 14 }}>
                <p style={{ fontWeight: 600, marginBottom: 10 }}>Nome della nuova sezione</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="inp"
                    style={{ flex: 1 }}
                    value={addTableName}
                    onChange={(e) => setAddTableName(e.target.value)}
                    placeholder="Es. Cereali, Vini, Formaggi…"
                    onKeyDown={(e) => { if (e.key === "Enter") addTable(); if (e.key === "Escape") setShowAddTable(false); }}
                  />
                  <button style={{ background: "#1A6B3A", color: "#fff", borderRadius: 9, padding: "0 18px", fontSize: 14, fontWeight: 700 }} onClick={addTable}>✓ Crea</button>
                  <button style={{ background: "#F0EDE8", color: "#6B6B65", borderRadius: 9, padding: "0 14px", fontSize: 14 }} onClick={() => setShowAddTable(false)}>✕</button>
                </div>
              </div>
            ) : (
              <button
                style={{ background: "none", border: "2px dashed #C0B8A8", borderRadius: 12, padding: 16, fontSize: 14, fontWeight: 600, color: "#6B6B65", width: "100%", marginBottom: 20 }}
                onClick={() => setShowAddTable(true)}
              >+ Aggiungi nuova sezione</button>
            )}
            <p style={{ textAlign: "center", fontSize: 11, color: "#B0A898", marginTop: 8 }}>Modifiche salvate automaticamente</p>
          </>
        )}

        {/* ─── TAB: IMPOSTAZIONI ─── */}
        {tab === "settings" && (
          <div className="fu" style={{ background: "#fff", borderRadius: 16, border: "1px solid #DDDAD4", padding: 28 }}>
            <h3 style={{ fontSize: 20, marginBottom: 20 }}>Impostazioni profilo</h3>

            {[
              { key: "name",     label: "Nome attività",  type: "text"     },
              { key: "bio",      label: "Descrizione",    type: "textarea" },
              { key: "delivery", label: "Info consegne",  type: "text"     },
              { key: "email",    label: "Email",          type: "email"    },
            ].map(({ key, label, type }) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <FieldLabel>{label}</FieldLabel>
                {type === "textarea" ? (
                  <textarea className="inp" style={{ resize: "vertical", minHeight: 72 }} value={supplier[key] || ""} onChange={(e) => update((s) => ({ ...s, [key]: e.target.value }))} />
                ) : (
                  <input className="inp" type={type} value={supplier[key] || ""} onChange={(e) => update((s) => ({ ...s, [key]: e.target.value }))} />
                )}
              </div>
            ))}

            {/* WhatsApp */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Numero WhatsApp ★ — dove ricevi gli ordini</FieldLabel>
              <PhoneInput
                value={supplier.waNumber || ""}
                country={supplier.waCountry || "IT"}
                onValueChange={(v) => update((s) => {
                  const c = COUNTRIES.find((x) => x.code === (s.waCountry || "IT")) || COUNTRIES[0];
                  return { ...s, waNumber: v, whatsapp: c.prefix + v };
                })}
                onCountryChange={(code) => update((s) => {
                  const c = COUNTRIES.find((x) => x.code === code) || COUNTRIES[0];
                  return { ...s, waCountry: code, whatsapp: c.prefix + (s.waNumber || "") };
                })}
              />
            </div>

            {/* Payment methods */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Metodi di pagamento</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                {supplier.payments.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ flex: 1, background: "#F5F2EC", borderRadius: 8, padding: "8px 12px", fontSize: 14 }}>{p}</span>
                    <button onClick={() => update((s) => ({ ...s, payments: s.payments.filter((_, j) => j !== i) }))} style={{ color: "#A04030", fontSize: 16, padding: "2px 6px" }}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="inp"
                  value={newPm}
                  onChange={(e) => setNewPm(e.target.value)}
                  placeholder="Es. Satispay, PayPal…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newPm.trim()) {
                      update((s) => ({ ...s, payments: [...s.payments, newPm.trim()] }));
                      setNewPm("");
                    }
                  }}
                  style={{ flex: 1, fontSize: 13 }}
                />
                <button
                  style={{ background: "#1A6B3A", color: "#fff", borderRadius: 8, padding: "0 14px", fontSize: 13, fontWeight: 700 }}
                  onClick={() => {
                    if (newPm.trim()) {
                      update((s) => ({ ...s, payments: [...s.payments, newPm.trim()] }));
                      setNewPm("");
                    }
                  }}
                >+</button>
              </div>
            </div>

            {/* Language */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Lingua principale — catalogo e messaggi WhatsApp</FieldLabel>
              <select className="inp" value={supplier.defaultLang || "it"} onChange={(e) => update((s) => ({ ...s, defaultLang: e.target.value }))}>
                {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <p style={{ fontSize: 11, color: "#9A9A90", marginTop: 4 }}>
                📱 I messaggi WhatsApp degli ordini arriveranno in questa lingua, indipendentemente dalla lingua scelta dal cliente nel catalogo.
              </p>
            </div>

            {/* Password change */}
            <div style={{ marginBottom: 16, borderTop: "1px solid #EDEAE4", paddingTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showPwSection ? 12 : 0 }}>
                <FieldLabel>Password admin</FieldLabel>
                <button style={{ fontSize: 13, fontWeight: 600, color: "#1A6B3A" }} onClick={() => { setShowPwSection(!showPwSection); setPwErr(""); }}>
                  {showPwSection ? "Annulla" : "Cambia password"}
                </button>
              </div>
              {showPwSection && (
                <div className="fu" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                  {[
                    { k: "current", ph: "Password attuale" },
                    { k: "next",    ph: "Nuova password (min. 6 caratteri)" },
                    { k: "confirm", ph: "Conferma nuova password" },
                  ].map(({ k, ph }) => (
                    <input key={k} className="inp" type="password" placeholder={ph} value={pwForm[k]} onChange={(e) => setPwForm((f) => ({ ...f, [k]: e.target.value }))} />
                  ))}
                  {pwErr && <div style={{ fontSize: 12, color: "#D04030" }}>⚠ {pwErr}</div>}
                  <button style={{ background: "#0A2A1A", color: "#fff", borderRadius: 9, padding: 10, fontSize: 13, fontWeight: 700 }} onClick={changePw}>
                    ✓ Aggiorna password
                  </button>
                </div>
              )}
            </div>

            {/* Slug read-only */}
            <div style={{ padding: "12px 16px", background: "#F5F2EC", borderRadius: 10, border: "1px dashed #C0B8A8" }}>
              <FieldLabel>URL catalogo (non modificabile)</FieldLabel>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1A6B3A", marginTop: 4 }}>
                easyorderhub.com/<strong>{supplier.slug}</strong>
              </div>
            </div>

            {/* Salva profilo su Supabase — Fix #6: sync esplicita solo dal tab Impostazioni */}
            {supplierId && (
              <button
                style={{ marginTop: 16, background: "#0A2A1A", color: "#fff", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 700, width: "100%" }}
                onClick={() => { syncProfileToSupabase(supplier); alert("✅ Profilo salvato su tutti i dispositivi."); }}
              >
                ☁️ Salva profilo nel cloud
              </button>
            )}
          </div>
        )}

        {/* ─── TAB: ORDINI — PRO ONLY ─── */}
        {tab === "orders" && (
          isPro ? (
            <div className="fu">
              {pending.length === 0 && confirmed.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#9A9A90", background: "#fff", borderRadius: 16, border: "1px solid #DDDAD4" }}>
                  <div style={{ fontSize: 48, marginBottom: 14 }}>📬</div>
                  <p style={{ fontWeight: 600, fontSize: 16 }}>Nessun ordine in attesa</p>
                  <p style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
                    Gli ordini appariranno qui automaticamente.<br />
                    Lo <strong>stock viene aggiornato</strong> al momento dell'ordine. Usa "Conferma" per segnare l'ordine come preso in carico.
                  </p>
                </div>
              ) : (
                <>
                  {pending.length > 0 && (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18 }}>In attesa di conferma</h3>
                        <span style={{ background: "#C07B30", color: "#fff", borderRadius: 10, fontSize: 11, fontWeight: 700, padding: "2px 8px" }}>{pending.length}</span>
                      </div>
                      {pending.map((ord) => (
                        <div key={ord.id} style={{ background: "#fff", borderRadius: 14, border: "2px solid #E8C880", marginBottom: 12, overflow: "hidden" }}>
                          <div style={{ padding: "14px 18px", borderBottom: "1px solid #F5EDD8", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 15 }}>{ord.customer.name}</div>
                              <div style={{ fontSize: 12, color: "#9A9A90", marginTop: 2 }}>{ord.customer.address}</div>
                              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                                <span style={{ fontSize: 11, color: "#1A6B3A", fontWeight: 600, background: "#E4F4E8", borderRadius: 6, padding: "2px 8px" }}>
                                  📞 {ord.customer.phone}
                                </span>
                                <span style={{ fontSize: 11, color: "#9A9A90" }}>
                                  {new Date(ord.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                </span>
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 700, color: "#C07B54" }}>
                                €{ord.total.toFixed(2)}
                              </div>
                              <div style={{ fontSize: 11, color: "#9A9A90", marginTop: 2 }}>{ord.customer.payment}</div>
                            </div>
                          </div>
                          <div style={{ padding: "10px 18px" }}>
                            {ord.items.map((item, i) => (
                              <div key={i} style={{ fontSize: 13, padding: "3px 0", display: "flex", justifyContent: "space-between" }}>
                                <span>{item.emoji} {item.qty}× {item.name} ({item.unit})</span>
                                <span style={{ fontWeight: 600, color: "#C07B54" }}>€{(item.price * item.qty).toFixed(2)}</span>
                              </div>
                            ))}
                            {ord.customer.notes && (
                              <div style={{ fontSize: 12, color: "#6B6B65", marginTop: 8, padding: "8px 10px", background: "#F5F2EC", borderRadius: 8 }}>
                                📝 {ord.customer.notes}
                              </div>
                            )}
                          </div>
                          <div style={{ padding: "12px 18px", borderTop: "1px solid #F5EDD8", display: "flex", gap: 8 }}>
                            <button
                              style={{ flex: 1, background: "#1A6B3A", color: "#fff", borderRadius: 10, padding: 11, fontSize: 13, fontWeight: 700 }}
                              onClick={() => confirmOrder(ord.id)}
                            >
                              ✅ Conferma — segna come preso in carico
                            </button>
                            <button
                              style={{ background: "#FEF2F2", color: "#A04030", borderRadius: 10, padding: "11px 14px", fontSize: 13, fontWeight: 600 }}
                              onClick={() => dismissOrder(ord.id)}
                            >✕</button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {confirmed.length > 0 && (
                    <>
                      <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 16, color: "#9A9A90", marginBottom: 10, marginTop: pending.length > 0 ? 20 : 0 }}>
                        Confermati recentemente
                      </h3>
                      {confirmed.slice(-5).reverse().map((ord) => (
                        <div key={ord.id} style={{ background: "#F5FAF3", borderRadius: 12, border: "1px solid #C4E4C8", padding: "12px 16px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>✅ {ord.customer.name}</div>
                            <div style={{ fontSize: 12, color: "#9A9A90", marginTop: 2 }}>
                              {ord.items.length} articoli · €{ord.total.toFixed(2)} · Preso in carico
                            </div>
                          </div>
                          <button style={{ color: "#9A9A90", fontSize: 13, padding: "4px 8px" }} onClick={() => dismissOrder(ord.id)}>✕</button>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <ProGate
              feature="Pannello ordini Pro — gestisci e conferma gli ordini ricevuti"
              onUpgrade={() => alert("Upgrade disponibile al lancio commerciale")}
            />
          )
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   7. SUPER ADMIN — route /admin
   Password: eoh_admin_2025
══════════════════════════════════════════════════════════════════ */
const SA_PASSWORD = "eoh_admin_2025";

const MOCK_SUPPLIERS = [
  { id:"s1", slug:"la-bottega",     name:"La Bottega di Terra",  email:"info@labottega.it",     plan:"pro",     status:"active",    products:12, pendingOrders:3,  mrr:29, createdAt:"2025-03-10", lastLogin:"2025-06-14", whatsapp:"393331234567", country:"IT", orders_total:87  },
  { id:"s2", slug:"orto-di-marco",  name:"Orto di Marco",        email:"marco@ortodimarco.it",  plan:"base",    status:"active",    products:8,  pendingOrders:0,  mrr:19, createdAt:"2025-03-22", lastLogin:"2025-06-13", whatsapp:"393341234567", country:"IT", orders_total:34  },
  { id:"s3", slug:"fior-di-latte",  name:"Fior di Latte Srl",    email:"admin@fiordilatte.com", plan:"pro",     status:"active",    products:21, pendingOrders:7,  mrr:29, createdAt:"2025-04-01", lastLogin:"2025-06-14", whatsapp:"393351234567", country:"IT", orders_total:156 },
  { id:"s4", slug:"spezie-del-sole",name:"Spezie del Sole",      email:"sole@spezie.it",        plan:"starter", status:"active",    products:6,  pendingOrders:0,  mrr:0,  createdAt:"2025-04-15", lastLogin:"2025-06-10", whatsapp:"393361234567", country:"IT", orders_total:12  },
  { id:"s5", slug:"cantina-rossi",  name:"Cantina Rossi",        email:"vini@cantinarossi.it",  plan:"base",    status:"suspended", products:14, pendingOrders:0,  mrr:0,  createdAt:"2025-02-28", lastLogin:"2025-05-30", whatsapp:"393371234567", country:"IT", orders_total:28  },
  { id:"s6", slug:"miele-alpino",   name:"Miele Alpino",         email:"api@mielealpino.ch",    plan:"pro",     status:"active",    products:9,  pendingOrders:1,  mrr:29, createdAt:"2025-05-02", lastLogin:"2025-06-12", whatsapp:"41791234567",  country:"CH", orders_total:43  },
  { id:"s7", slug:"boulangerie-leo",name:"Boulangerie Léo",      email:"leo@boulangerie.fr",    plan:"base",    status:"trial",     products:5,  pendingOrders:0,  mrr:0,  createdAt:"2025-06-10", lastLogin:"2025-06-14", whatsapp:"33612345678",  country:"FR", orders_total:3   },
];

const MOCK_EVENTS = [
  { id:1, msg:"Nuovo fornitore: Boulangerie Léo (Base trial)",              time:"14 min fa",   severity:"info"    },
  { id:2, msg:"Fior di Latte Srl — 156 ordini totali (+12 oggi)",           time:"1 ora fa",    severity:"success" },
  { id:3, msg:"Miele Alpino: upgrade Base → Pro",                           time:"3 ore fa",    severity:"success" },
  { id:4, msg:"Cantina Rossi: pagamento fallito da 15 giorni",              time:"1 giorno fa", severity:"danger"  },
  { id:5, msg:"Deploy v8.0 completato — nessun errore",                     time:"3 giorni fa", severity:"success" },
];

function PlanBadge({ plan }) {
  const map = { starter: { name:"Starter", color:"#6B7280" }, base: { name:"Base", color:"#1A6B3A" }, pro: { name:"Pro", color:"#1A3A8F" } };
  const p = map[plan] || { name: plan, color: "#6B7280" };
  return (
    <span className="plan-badge" style={{ background: p.color + "22", color: p.color, border: "1px solid " + p.color + "44" }}>
      {p.name}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = { active: "badge-active", suspended: "badge-suspended", trial: "badge-trial" };
  const labels = { active: "● Attivo", suspended: "● Sospeso", trial: "◌ Trial" };
  return <span className={map[status] || "badge-trial"}>{labels[status] || status}</span>;
}

function SuperAdminLogin({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    document.body.classList.add("admin-mode");
    return () => document.body.classList.remove("admin-mode");
  }, []);

  function login() {
    if (pw === SA_PASSWORD) {
      onSuccess();
    } else {
      setErr("Credenziali non valide");
      setPw("");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0F1117", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="pi" style={{ background: "#161920", border: "1px solid #252830", borderRadius: 20, padding: 40, maxWidth: 380, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🛡️</div>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, marginBottom: 6, color: "#E8E6E0" }}>Platform Admin</h2>
        <p style={{ fontSize: 13, color: "#5A5A60", marginBottom: 28 }}>easyorderhub.com/admin</p>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <input
            style={{ width: "100%", padding: "11px 44px 11px 14px", border: "1px solid #2E3040", borderRadius: 9, fontSize: 15, background: "#0F1117", color: "#E8E6E0", outline: "none", textAlign: "center", fontFamily: "'Outfit', sans-serif" }}
            type={show ? "text" : "password"}
            placeholder="Password superadmin"
            value={pw}
            onChange={(e) => { setPw(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && login()}
          />
          <button style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#5A5A60" }} onClick={() => setShow(!show)}>
            {show ? "🙈" : "👁"}
          </button>
        </div>
        {err && <p style={{ color: "#E06060", fontSize: 13, marginBottom: 10 }}>{err}</p>}
        <button style={{ width: "100%", padding: 13, borderRadius: 10, background: "#3A6BFF", color: "#fff", fontSize: 15, fontWeight: 700 }} onClick={login}>
          Accedi al pannello
        </button>
        <p style={{ fontSize: 11, color: "#3A3A45", marginTop: 16 }}>Accesso riservato al proprietario della piattaforma</p>
        <button style={{ fontSize: 12, color: "#5A5A60", marginTop: 8 }} onClick={() => window.location.href = "/"}>
          ← Torna al sito
        </button>
      </div>
    </div>
  );
}

function SuperAdminShell() {
  const [view, setView] = useState("dashboard");
  const [suppliers, setSuppliers] = useState(MOCK_SUPPLIERS);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.body.classList.add("admin-mode");
    return () => document.body.classList.remove("admin-mode");
  }, []);

  const suspended = suppliers.filter((s) => s.status === "suspended").length;
  const trialCount = suppliers.filter((s) => s.status === "trial").length;
  const active = suppliers.filter((s) => s.status === "active");
  const mrr = active.reduce((s, x) => s + x.mrr, 0);
  const totalOrders = suppliers.reduce((s, x) => s + x.orders_total, 0);
  const proCount = active.filter((s) => s.plan === "pro").length;
  const baseCount = active.filter((s) => s.plan === "base").length;

  const filtered = useMemo(() => {
    return suppliers.filter((s) => {
      const q = search.toLowerCase();
      const matchQ = !q || s.name.toLowerCase().includes(q) || s.email.includes(q) || s.slug.includes(q);
      const matchP = planFilter === "all" || s.plan === planFilter;
      return matchQ && matchP;
    });
  }, [suppliers, search, planFilter]);

  function updateSupplier(upd) {
    if (upd._delete) {
      setSuppliers((ss) => ss.filter((s) => s.id !== upd.id));
    } else {
      setSuppliers((ss) => ss.map((s) => s.id === upd.id ? upd : s));
    }
  }

  const NAV = [
    { id: "dashboard", icon: "📊", label: "Dashboard"                    },
    { id: "suppliers", icon: "🏪", label: "Fornitori",    badge: true    },
    { id: "billing",   icon: "💰", label: "Abbonamenti"                  },
    { id: "system",    icon: "🖥",  label: "Sistema & Log"               },
  ];

  const severityColors = { success: "#60C870", danger: "#E06060", info: "#4A80FF", warning: "#E0B040" };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#0F1117", color: "#E8E6E0" }}>
      {/* Sidebar */}
      <aside className="sa-sidebar">
        <div style={{ padding: "20px 18px 14px", borderBottom: "1px solid #252830" }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 700, color: "#E8E6E0" }}>
            EOH<span style={{ fontWeight: 400, color: "#3A3A45" }}> /admin</span>
          </div>
          <div style={{ fontSize: 10, color: "#3A6BFF", fontWeight: 600, letterSpacing: ".08em", marginTop: 2 }}>PLATFORM CONTROL</div>
        </div>
        <nav style={{ flex: 1, padding: "10px 0", overflowY: "auto" }}>
          {NAV.map((item) => (
            <button key={item.id} className={"sa-nav-item" + (view === item.id ? " active" : "")} onClick={() => setView(item.id)}>
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
              {item.badge && (suspended + trialCount) > 0 && (
                <span className="sa-nav-badge">{suspended + trialCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 18px", borderTop: "1px solid #252830" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#60C870", animation: "pulse 2s ease-in-out infinite" }} />
            <span style={{ fontSize: 11, color: "#60C870", fontWeight: 600 }}>Tutti i sistemi operativi</span>
          </div>
          <button
            style={{ fontSize: 12, color: "#5A5A60", width: "100%", textAlign: "left" }}
            onClick={() => { if (window.confirm("Uscire dal pannello admin?")) window.location.href = "/"; }}
          >
            Torna al sito →
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, marginLeft: 220, padding: "28px", overflowX: "hidden" }}>
        {/* Topbar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: "clamp(18px,3vw,24px)", fontWeight: 700, color: "#E8E6E0" }}>
              {NAV.find((n) => n.id === view)?.icon} {NAV.find((n) => n.id === view)?.label}
            </h1>
            <p style={{ fontSize: 12, color: "#5A5A60", marginTop: 2 }}>
              easyorderhub.com/admin · {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>
          {suspended > 0 && (
            <div style={{ background: "#2A1A1A", border: "1px solid #5A2A2A", borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ color: "#E06060", fontWeight: 700 }}>⚠️ {suspended} account sospeso</span>
              <button style={{ color: "#4A80FF", fontWeight: 600, fontSize: 12 }} onClick={() => setView("billing")}>Verifica →</button>
            </div>
          )}
        </div>

        {/* DASHBOARD */}
        {view === "dashboard" && (
          <div className="fu">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 28 }}>
              {[
                ["Fornitori attivi",  active.length,     "Starter: " + suppliers.filter(s => s.plan === "starter").length + " · trial: " + trialCount, "#60C870"],
                ["MRR",              "€" + mrr,          proCount + " Pro · " + baseCount + " Base",                                                    "#4A80FF"],
                ["Ordini totali",    totalOrders,         "su tutta la piattaforma",                                                                     "#E8E6E0"],
                ["Sospesi",          suspended,           "pagamento fallito",                                                                           suspended > 0 ? "#E06060" : "#60C870"],
              ].map(([label, value, sub, color]) => (
                <div key={label} className="sa-card fu">
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#5A5A60", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
                  <div style={{ fontFamily: "'Fraunces', serif", fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
                  <div style={{ fontSize: 12, color: "#5A5A60", marginTop: 6 }}>{sub}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16 }}>
              {/* Piano breakdown */}
              <div className="sa-panel">
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #252830" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>Distribuzione piani</h3>
                </div>
                <div style={{ padding: 20 }}>
                  {[["starter","#6B7280"],["base","#1A6B3A"],["pro","#1A3A8F"]].map(([k, c]) => {
                    const count = suppliers.filter((s) => s.plan === k).length;
                    const pct   = Math.round((count / suppliers.length) * 100);
                    return (
                      <div key={k} style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: c, textTransform: "capitalize" }}>{k}</span>
                          <span style={{ fontSize: 13, color: "#5A5A60" }}>{count} ({pct}%)</span>
                        </div>
                        <div style={{ height: 6, background: "#252830", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: pct + "%", background: c, borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #252830" }}>
                    {[["Pro", proCount * 29, "#6090FF"], ["Base", baseCount * 19, "#60C870"]].map(([n, v, c]) => (
                      <div key={n} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}>
                        <span style={{ color: "#8A8880" }}>{n}</span>
                        <span style={{ fontWeight: 600, color: c }}>€{v}/mese</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", borderTop: "1px solid #252830", marginTop: 6, fontSize: 14, fontWeight: 700 }}>
                      <span>MRR Totale</span>
                      <span style={{ color: "#4A80FF" }}>€{mrr}/mese</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity feed */}
              <div className="sa-panel">
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #252830" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>Attività recente</h3>
                </div>
                <div style={{ padding: "8px 0" }}>
                  {MOCK_EVENTS.map((ev) => (
                    <div key={ev.id} style={{ display: "flex", gap: 10, padding: "10px 16px", borderBottom: "1px solid #1A1D25" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 3, background: severityColors[ev.severity] || "#4A80FF" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: "#C8C6C0", lineHeight: 1.5 }}>{ev.msg}</div>
                        <div style={{ fontSize: 11, color: "#4A4A55", marginTop: 3 }}>{ev.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SUPPLIERS */}
        {view === "suppliers" && (
          <div className="fu">
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <input className="sa-inp" style={{ flex: "1 1 200px", maxWidth: 300 }} placeholder="Cerca nome, email, slug…" value={search} onChange={(e) => setSearch(e.target.value)} />
              <select className="sa-inp" style={{ width: "auto" }} value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                <option value="all">Tutti i piani</option>
                {["starter","base","pro"].map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
              <button style={{ background: "#3A6BFF", color: "#fff", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700 }} onClick={() => alert("Invita fornitore — da collegare al sistema email")}>
                + Invita
              </button>
            </div>
            <div className="sa-panel" style={{ overflowX: "auto" }}>
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Fornitore</th>
                    <th>Piano</th>
                    <th>Stato</th>
                    <th>Prodotti</th>
                    <th>Ordini</th>
                    <th>MRR</th>
                    <th>Ultimo accesso</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: "#5A5A60", marginTop: 2 }}>/{s.slug} · {s.email}</div>
                      </td>
                      <td><PlanBadge plan={s.plan} /></td>
                      <td><StatusBadge status={s.status} /></td>
                      <td style={{ fontWeight: 600 }}>{s.products}</td>
                      <td style={{ color: "#4A80FF", fontWeight: 600 }}>{s.orders_total}</td>
                      <td style={{ fontWeight: 700, color: s.mrr > 0 ? "#60C870" : "#5A5A60" }}>{s.mrr > 0 ? "€" + s.mrr : "—"}</td>
                      <td style={{ fontSize: 12, color: "#5A5A60" }}>{s.lastLogin}</td>
                      <td>
                        <button
                          onClick={() => setSelected({ ...s })}
                          style={{ background: "#1E2230", border: "1px solid #2E3040", color: "#8A8880", borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 600 }}
                        >Gestisci</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Supplier modal */}
            {selected && (
              <div className="sa-modal-overlay" onClick={(e) => e.target === e.currentTarget && setSelected(null)}>
                <div className="sa-modal-box pi">
                  <div style={{ padding: "18px 24px", borderBottom: "1px solid #252830", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#E8E6E0" }}>{selected.name}</h3>
                      <div style={{ fontSize: 12, color: "#5A5A60", marginTop: 2 }}>/{selected.slug}</div>
                    </div>
                    <button style={{ fontSize: 20, color: "#5A5A60", lineHeight: 1 }} onClick={() => setSelected(null)}>✕</button>
                  </div>
                  <div style={{ padding: 24 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                      {[["Nome", "name"], ["Email", "email"], ["WhatsApp", "whatsapp"]].map(([l, k]) => (
                        <div key={k}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#5A5A60", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{l}</div>
                          <input className="sa-inp" value={selected[k] || ""} onChange={(e) => setSelected((s) => ({ ...s, [k]: e.target.value }))} />
                        </div>
                      ))}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#5A5A60", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Piano</div>
                        <select className="sa-inp" value={selected.plan} onChange={(e) => setSelected((s) => ({ ...s, plan: e.target.value }))}>
                          {["starter","base","pro"].map((p) => (
                            <option key={p} value={p}>{p} — €{{ starter:0, base:19, pro:29 }[p]}/mese</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#5A5A60", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>Stato</div>
                        <select className="sa-inp" value={selected.status} onChange={(e) => setSelected((s) => ({ ...s, status: e.target.value }))}>
                          {["active","suspended","trial"].map((v) => <option key={v}>{v}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 16 }}>
                      {[["Prodotti", selected.products], ["Ordini", selected.orders_total], ["MRR", "€" + selected.mrr]].map(([l, v]) => (
                        <div key={l} style={{ background: "#0F1117", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "#5A5A60", textTransform: "uppercase", letterSpacing: ".05em" }}>{l}</div>
                          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 700, marginTop: 4, color: "#E8E6E0" }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                      {[
                        { label: "Apri catalogo",               color: "#3A6BFF", fn: () => window.open("/", "_blank") },
                        { label: "Invia email supporto",         color: "#60C870", fn: () => window.open("mailto:" + selected.email) },
                        { label: selected.status === "suspended" ? "Riattiva account" : "Sospendi account",
                          color: selected.status === "suspended" ? "#60C870" : "#E0B040",
                          fn: () => setSelected((s) => ({ ...s, status: s.status === "suspended" ? "active" : "suspended" })) },
                      ].map(({ label, color, fn }) => (
                        <button key={label} onClick={fn} style={{ padding: "12px 16px", borderRadius: 10, background: "#0F1117", border: "1px solid " + color + "33", textAlign: "left", fontSize: 14, fontWeight: 600, color }}>
                          {label}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #2E3040", fontSize: 13, color: "#8A8880" }} onClick={() => setSelected(null)}>Annulla</button>
                      <button style={{ padding: "9px 18px", borderRadius: 8, background: "#3A6BFF", color: "#fff", fontSize: 13, fontWeight: 700 }} onClick={() => { updateSupplier(selected); setSelected(null); }}>Salva modifiche</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BILLING */}
        {view === "billing" && (
          <div className="fu">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 24 }}>
              {[
                ["MRR",             "€" + mrr,                                                                              "#4A80FF"],
                ["ARR",             "€" + (mrr * 12),                                                                       "#60C870"],
                ["Abbonati",        active.filter((s) => s.mrr > 0).length,                                                "#E8E6E0"],
                ["ARPU",            "€" + (active.filter(s=>s.mrr>0).length ? Math.round(mrr / active.filter(s=>s.mrr>0).length) : 0), "#E0B040"],
              ].map(([l, v, c]) => (
                <div key={l} className="sa-card">
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#5A5A60", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>{l}</div>
                  <div style={{ fontFamily: "'Fraunces', serif", fontSize: 30, fontWeight: 700, color: c, lineHeight: 1 }}>{v}</div>
                </div>
              ))}
            </div>

            <div className="sa-panel" style={{ overflowX: "auto", marginBottom: 20 }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #252830", fontSize: 15, fontWeight: 600 }}>Abbonamenti attivi</div>
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Fornitore</th>
                    <th>Piano</th>
                    <th>Importo</th>
                    <th>Stato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {active.filter((s) => s.mrr > 0).map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: "#5A5A60" }}>{s.email}</div>
                      </td>
                      <td><PlanBadge plan={s.plan} /></td>
                      <td style={{ fontWeight: 700, color: "#60C870" }}>€{s.mrr}/mese</td>
                      <td><span className="badge-active">● Pagato</span></td>
                      <td>
                        <button
                          style={{ background: "#1E2230", border: "1px solid #2E3040", color: "#8A8880", borderRadius: 7, padding: "4px 10px", fontSize: 11 }}
                          onClick={() => alert("Apertura Stripe — da collegare")}
                        >Stripe →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {suspended > 0 && (
              <div className="sa-panel">
                <div style={{ padding: "14px 20px", borderBottom: "1px solid #252830", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#E0B040" }}>Pagamenti falliti</span>
                </div>
                {suppliers.filter((s) => s.status === "suspended").map((s) => (
                  <div key={s.id} style={{ padding: "14px 20px", borderBottom: "1px solid #1A1D25", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "#5A5A60", marginTop: 2 }}>Sospeso · Ultimo accesso: {s.lastLogin}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={{ background: "#1A3A1A", color: "#60C870", border: "1px solid #2A5A2A", borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 600 }} onClick={() => alert("Email sollecito inviata a " + s.email)}>
                        📧 Sollecita
                      </button>
                      <button style={{ background: "#1E2230", border: "1px solid #2E3040", color: "#8A8880", borderRadius: 7, padding: "5px 12px", fontSize: 12 }} onClick={() => alert("Apertura Stripe")}>
                        Stripe →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SYSTEM */}
        {view === "system" && (
          <div className="fu">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 20 }}>
              {[
                ["Supabase DB",     "42ms"],
                ["Stripe Billing",  "120ms"],
                ["Anthropic API",   "890ms"],
                ["Netlify CDN",     "18ms"],
                ["GitHub Sync",     "OK"],
              ].map(([name, latency]) => (
                <div key={name} className="sa-card" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#60C870", flexShrink: 0, animation: "pulse 2s ease-in-out infinite" }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#E8E6E0" }}>{name}</div>
                    <div style={{ fontSize: 11, color: "#5A5A60", marginTop: 2 }}>Operativo · {latency}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="sa-panel">
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #252830", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>Log di sistema</span>
                <button style={{ fontSize: 12, color: "#5A5A60", border: "1px solid #2E3040", borderRadius: 7, padding: "4px 10px" }} onClick={() => alert("Export log CSV")}>📥 Esporta</button>
              </div>
              <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                {[
                  ["2025-06-14 14:32", "INFO",  "Claude API translation — supplier: fior-di-latte, lang: en"],
                  ["2025-06-14 13:18", "INFO",  "New signup: boulangerie-leo — plan: base, country: FR"],
                  ["2025-06-14 12:44", "WARN",  "WhatsApp order event — supplier cantina-rossi offline"],
                  ["2025-06-14 11:00", "INFO",  "Supabase health check: OK — latency 42ms"],
                  ["2025-06-13 23:55", "INFO",  "Stripe webhook: invoice.paid — miele-alpino — €29"],
                  ["2025-06-13 22:10", "ERROR", "Stripe: payment_failed — cantina-rossi — retry 1/3"],
                  ["2025-06-13 20:05", "INFO",  "Deploy v8.0 — build OK — 0 errors"],
                ].map(([time, level, msg], i) => (
                  <div key={i} style={{ display: "flex", gap: 12, padding: "9px 16px", borderBottom: "1px solid #1A1D25", alignItems: "flex-start" }}>
                    <span style={{ color: "#3A3A45", flexShrink: 0, whiteSpace: "nowrap" }}>{time}</span>
                    <span style={{ fontWeight: 700, color: { INFO: "#4A80FF", WARN: "#E0B040", ERROR: "#E06060" }[level], flexShrink: 0, width: 44 }}>{level}</span>
                    <span style={{ color: "#A0A0A8" }}>{msg}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SuperAdminApp() {
  const [authed, setAuthed] = useState(false);
  if (!authed) return <SuperAdminLogin onSuccess={() => setAuthed(true)} />;
  return <SuperAdminShell />;
}

/* ══════════════════════════════════════════════════════════════════
   APP ROOT
══════════════════════════════════════════════════════════════════ */
function MainApp() {
  const [page,        setPage]        = useState("home");
  const [supplier,    setSupplier]    = useState(() => loadSupplier("la-bottega"));
  const [cart,        setCart]        = useState({});
  const [adminOk,     setAdminOk]     = useState(false);
  const [adminUserId, setAdminUserId] = useState(null);

  /* FIX 4.1 — debounce 500ms */
  useEffect(() => {
    const timer = setTimeout(() => { saveSupplier(supplier); }, 500);
    return () => clearTimeout(timer);
  }, [supplier]);

  /* URL routing: se l'URL contiene uno slug, carica quel catalogo.
     Fix #4 Opus: usa dbGetSupplierIdBySlug per verificare che lo slug esista
     su Supabase (non mostrare catalogo demo per slug inesistenti).
     Fix #1 Opus: recupera il supplierId e lo scrive nel supplier object
     così CartDrawer può salvare gli ordini su Supabase. */
  useEffect(() => {
    const path = window.location.pathname;
    const slug = path.split('/').filter(Boolean)[0];
    if (!slug || slug === 'admin' || slug === 'register') return;

    const data = loadSupplier(slug);

    /* Se il supplier ha già il supplierId in localStorage, carica direttamente */
    if (data.supplierId) {
      setSupplier(data);
      setPage("catalog");
      return;
    }

    /* Altrimenti verifica su Supabase se lo slug esiste */
    dbGetSupplierIdBySlug(slug).then((id) => {
      if (id) {
        /* Slug valido — aggiorna il supplier con il supplierId e carica */
        const updated = { ...data, supplierId: id };
        saveSupplier(updated);
        setSupplier(updated);
        setPage("catalog");
      } else if (data.name !== "La Bottega di Terra") {
        /* Slug presente solo in localStorage (utente non ancora su Supabase) */
        setSupplier(data);
        setPage("catalog");
      } else if (slug === "la-bottega") {
        /* Eccezione: la demo è sempre accessibile via link diretto,
           anche se lo slug non esiste ancora su Supabase */
        setSupplier(data);
        setPage("catalog");
      } else {
        /* Slug inesistente — mostra home invece di catalogo demo fittizio */
        setPage("home");
        if (window.history && window.history.pushState) {
          window.history.pushState({}, '', '/');
        }
      }
    }).catch(() => {
      /* In caso di errore di rete, usa i dati locali se presenti */
      if (data.name !== "La Bottega di Terra") {
        setSupplier(data);
        setPage("catalog");
      }
    });
  }, []);

  function goToSlug(slug) {
    const data = loadSupplier(slug);
    setSupplier(data);
    setCart({});
    setAdminOk(false);
    setAdminUserId(null);
    setPage("catalog");
    if (window.history && window.history.pushState) {
      window.history.pushState({}, '', '/' + slug);
    }
  }

  /* Fix #3 Opus — Browser back button: ascolta popstate per sincronizzare
     la navigazione React con il tasto ← del browser */
  useEffect(() => {
    function handlePopState() {
      const path = window.location.pathname;
      const slug = path.split('/').filter(Boolean)[0];
      if (!slug || slug === 'admin') {
        setPage("home");
      } else if (slug === 'register') {
        setPage("register");
      } else {
        const data = loadSupplier(slug);
        setSupplier(data);
        setCart({});
        setAdminOk(false);
        setAdminUserId(null);
        setPage("catalog");
      }
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /* FIX N2/N4 — handleOrderPlaced aggiorna lo state React E il localStorage
     in modo atomico. Salva anche su Supabase se il supplierId è disponibile. */
  function handleOrderPlaced(freshData) {
    saveSupplier(freshData);
    setSupplier(freshData);
  }

  return (
    <>
      {page === "home" && (
        <PlatformHome
          onDemo={() => goToSlug("la-bottega")}
          onRegister={() => setPage("register")}
        />
      )}
      {page === "register" && (
        <RegistrationForm
          onSuccess={(slug) => goToSlug(slug)}
          onBack={() => setPage("home")}
        />
      )}
      {page === "catalog" && (
        <SupplierCatalog
          supplier={supplier}
          cart={cart}
          setCart={setCart}
          onAdmin={() => adminOk ? setPage("admin") : setPage("admin_login")}
          onHome={() => { setPage("home"); window.history.pushState({}, '', '/'); }}
          onOrderPlaced={handleOrderPlaced}
        />
      )}
      {page === "admin_login" && (
        <AdminLogin
          supplier={supplier}
          onSuccess={(userId) => { setAdminUserId(userId); setAdminOk(true); setPage("admin"); }}
          onBack={() => setPage("catalog")}
        />
      )}
      {page === "admin" && adminOk && (
        <AdminPanel
          supplier={supplier}
          setSupplier={setSupplier}
          onBack={() => setPage("catalog")}
          supplierId={adminUserId}
        />
      )}
    </>
  );
}

/* FIX 6.7 — ErrorBoundary wrappa tutta l'app */
export default function App() {
  return (
    <ErrorBoundary>
      <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />
      {IS_ADMIN_ROUTE ? <SuperAdminApp /> : <MainApp />}
    </ErrorBoundary>
  );
}